import { tileInteractionsAt } from "game-common/entity_tile_interactions"
import {
    Coordinate,
    Entity,
    EntityId,
    getCoordinateDistance,
    getDistance,
    getEntityDistance,
    Latch,
    MovementInertiaConfig,
    Orientation,
    randomIntBetween,
    TileSize,
    WeaponCharacteristics,
} from "game-common/models"
import { logger } from "./logger"
import {
    blankCoordinate,
    calculateNewCoordinateFromRadians,
    calculatePointAtDistance,
    Callback,
    clearCoordinate,
    degrees_to_radians,
    emptyCoordinate,
    fineToRough,
    fineToRoughCoordinates,
    floorCoordinates,
    getAngle,
    isEqual,
    radians_to_degrees,
    randomId,
    roughToFine,
    roughToFineCoordinates,
    roughToFineCoordinatesOffset,
    roundCoordinates,
} from "game-common/util"

import { Mechanics } from "game-common/mechanics/mechanics"
import { clampPlacementTarget, facingPlacementTarget } from "game-common/item/placement"
import { BUILDING_SHAPES, BuildingShape, buildingShapeCells } from "./building_shapes"

import { InteractionDescriptor, InteractionType, wheelInteractions } from "game-common/mechanics/entity_mechanics"

import { Client } from "./client"
import { IKeyboard, IMouse, KeyModifiers, MouseState } from "./client_models"
import { isMobile, ScalingParams } from "./client_util"
import { MapRenderingManager } from "./rendering/impl/pixijs/map_rendering_manager"
import { Fader } from "./rendering/impl/pixijs/gui/fader"
import { MapEntry } from "game-common/room/base_map_builder"
import { Graphics, Sprite } from "pixi.js"

// Touch holds open the context menu; a short touch remains an attack.
/**
 * The largest step accrueRunHint will credit in one tick of the velocity loop.
 *
 * That loop is a setInterval, and a browser throttles those hard in a backgrounded tab -- so without
 * this, a tab left alone with Shift down would return having banked the whole threshold at once.
 */
const RUN_HINT_MAX_STEP_MS = 250

const ENTITY_PRESS_HOLD_MS = 500
const HELD_ATTACK_MS = 400

const BUILD_PENDING_MS = 5000
const BUILD_GHOST_ALPHA = 0.25

interface PendingBuild {
    coordinate: Coordinate
    itemId: string
    roomId: string
    startedAt?: number
    sprites: Set<Sprite>
    shortageMarker?: Graphics
    unfunded?: boolean
    invalidMarker?: Graphics
    valid: boolean
    previewResolved?: boolean
    committing: boolean
    groupId?: number
}

interface SharedBuild {
    startedAt?: number
    sprites: Set<Sprite>
}

// An armed location pick: the caption shown under the reticle, and what to do with the fine world
// coordinate the player settles on. See PlayerController.beginLocationPick.
export interface LocationPickRequest {
    // The line under the reticle, and what replaces it over ground nothing can stand on.
    caption: string
    blockedCaption: string
    onPick: (point: Coordinate) => void
}

class DegreesRotator {
    targetAngle: number
    increment: number
    currentAngle: number

    constructor(targetAngle: number, increment: number, currentAngle: number) {
        this.targetAngle = targetAngle
        this.currentAngle = currentAngle
        this.increment = increment

        const delta = this.targetAngle - this.currentAngle

        if (Math.abs(delta) >= 180) {
            if (this.targetAngle > this.currentAngle) {
                this.currentAngle += 360
            } else {
                this.targetAngle += 360
            }
        }
    }

    update = (): boolean => {
        const delta = this.targetAngle - this.currentAngle
        if (delta < 0) {
            this.currentAngle -= this.increment
        } else {
            this.currentAngle += this.increment
        }

        if (Math.abs(delta) < this.increment) {
            return true
        }
        return false
    }

    normalizedCurrent = () => {
        return this.currentAngle > 360 ? this.currentAngle - 360 : this.currentAngle
    }

    normalizedTarget = () => {
        return this.targetAngle > 360 ? this.targetAngle - 360 : this.targetAngle
    }
}

export class Player {
    client: Client
    entity: Entity
    private mouseHandler: IMouse
    private keyboardHandler: IKeyboard
    private isChat: boolean = false
    private lastMouseMoveTs: number = 0
    private keyboardActionEnabled: boolean = true
    private mouseActionEnabled: boolean = true
    private suppressAttackUntilTs: number = 0
    private paralyzed: boolean
    /** The furthest-out deadline any outstanding paralyze request has asked for. See paralyze(). */
    private paralyzedUntilTs: number = 0
    // DOWNED: the server's verdict that this player has fallen and has not been put back on their
    // feet yet (Message.downed, mirrored by ClientGameLogic.updateFromPlayerMessage). Distinct
    // from `paralyzed`, which is a short client-side beat that times itself out -- this one lasts
    // as long as the server says it does, which for an ember camp is however long the player takes
    // to answer their end-of-run panel. The server refuses their input for the same reason, so
    // stopping here is what keeps them from predicting a walk they never get to take.
    private downed: boolean = false
    private randomness: any = undefined
    private isFiring: boolean
    private localWeaponCharge?: { id: string; profileId: string; itemId: string }
    private mouseAttackHeld: boolean = false
    private lastPrimaryItemUseTs: number | undefined
    private fireInterval: any
    private guest: boolean
    private targetKeyboardAngle: number
    private keyboardAndleAdjustInterval: any
    private pointer: Coordinate
    private roughPointer: Coordinate
    private roughCompositeTilePointer: Coordinate = blankCoordinate()
    private movementDaemon: any
    private longRangedEquipped: boolean = undefined
    private mousePointerFadeInterval: any
    private mousePointerFader: Fader
    private terrainPointerOn: boolean = false
    private buildingEquipmentRestored = false
    private buildPointer?: Coordinate
    private buildGridMarker?: Graphics
    private placementPointerOn = false
    private placementPointer?: Coordinate
    private placementPointerPose?: { x: number; y: number; angle: number }
    private placementPointerItemId?: string
    private lastPlacementMousePosition?: Coordinate
    private pendingPrintTimers = new Map<number, ReturnType<typeof setTimeout>>()
    private buildStroke?: { last: Coordinate; visited: Set<string>; area?: { anchor: Coordinate; cells: Set<string>; hollow: boolean; shape: BuildingShape } }
    private mobileBuildTool: "single" | "line" | "room" = "single"
    private mobileBuildDragging = false
    private buildingShape: BuildingShape = "rectangle"
    private buildingShapePaletteDismissed = false
    private buildingShapeScroll = 0
    private buildingShapeScrollAt = 0
    private buildPrintGroupId = 0
    private buildPreviewRevision = 0
    private pendingBuilds = new Map<string, PendingBuild>()
    private sharedBuilds = new Map<EntityId, { receivedAt: number; tiles: SharedBuild[] }>()
    private lastSharedBuildSnapshot = ""
    private lastSharedBuildAt = 0
    private buildPreview?: { coordinate: Coordinate; deltas: MapEntry[]; valid: boolean }
    private terrainPointerSprites: Set<Sprite> = new Set()
    // LOCATION PICK: an armed "click a spot on the ground" gesture (the defender's "Hold Position"
    // order). While it is set the pointer means "here" rather than "attack" -- every world press,
    // every entity press and every build commit is held back for it, and the click that lands
    // answers it instead. Cleared by the pick itself, by Escape, or by anything that takes the
    // player's hands off the world (downed, paralyzed, room change).
    private locationPick?: LocationPickRequest
    private isAutoMoving: boolean
    private hoveredEntity: EntityId
    // A touch being held for a menu, and whether that hold already opened it.
    private tilePressTimer?: ReturnType<typeof setTimeout>
    private pressedTile?: Coordinate
    private pressedEntityId?: EntityId
    private pressOpenedMenu: boolean = false
    private pressHoldTimer?: ReturnType<typeof setTimeout>
    private autoMovingInterval: any
    private velocityUpdateInterval: any
    private targetSpeed2: Coordinate = { x: 0, y: 0 }
    // How long the player has genuinely run -- Shift held AND moving -- while the camp's run hint
    // is teachable. See accrueRunHint.
    private runHintMs = 0
    private runHintLastTs = 0

    // Held movement intent from the mobile joysticks. Their onChange only fires on pointermove,
    // so a thumb parked at full deflection produces no events at all -- the intent has to be
    // latched here and re-applied by updatePlayerVelocity every tick, exactly as a held key is.
    private joystickSpeed2: Coordinate | null = null
    private movementStartTime: number = 0
    private isSustainedMovement: boolean = false

    constructor(client: Client, entity: Entity, mouseHandler: IMouse, keyboardHandler: IKeyboard, guest: boolean) {
        this.client = client
        this.entity = entity
        this.mouseHandler = mouseHandler
        this.keyboardHandler = keyboardHandler
        this.guest = guest

        keyboardHandler.addListener(this.respondToKeyBoard)
        keyboardHandler.addPasteListener(this.respondToPaste)
        mouseHandler.addListener(this.respondToMouse)
        document.addEventListener("wheel", this.respondToBuildShapeWheel, { passive: false })
        window.addEventListener("blur", this.cancelHeldAttack)

        // Start velocity update loop
        this.startVelocityUpdates()
    }

    private isKeyOn = (key: string) => {
        // If in chat mode, don't detect movement keys
        if (this.isChat && ["w", "a", "s", "d", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
            return false
        }
        return this.keyboardHandler.state().get(key) || this.keyboardHandler.state().get(key.toUpperCase())
    }
    private isButtonOn = (key: string) => this.mouseHandler.state().buttons.get(key) == true

    adjustEntityAngle = (entity: Entity, delta: Coordinate) =>
        getAngle(
            { x: entity.location.x, y: entity.location.y },
            { x: entity.location.x + delta.x, y: entity.location.y + delta.y },
        )

    private keyboardBasedAngle = (entity: Entity) => {
        const leftRightOn =
            this.isKeyOn("a") || this.isKeyOn("ArrowLeft") || this.isKeyOn("d") || this.isKeyOn("ArrowRight")
        const upDownOn = this.isKeyOn("w") || this.isKeyOn("ArrowUp") || this.isKeyOn("s") || this.isKeyOn("ArrowDown")

        let newTargetAngle

        if (!leftRightOn) {
            if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: 0, y: 1 })
            }
            if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: 0, y: -1 })
            }
        } else {
            if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: 1 })
                }
                if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: 1 })
                }
            }
            if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: -1 })
                }
                if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: -1 })
                }
            }
        }

        if (!upDownOn) {
            if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: 0 })
            }
            if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: 0 })
            }
        } else {
            if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: 1 })
                }
                if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: -1 })
                }
            }
            if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: 1 })
                }
                if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: -1 })
                }
            }
        }

        if (newTargetAngle !== undefined && newTargetAngle !== this.targetKeyboardAngle) {
            if (this.keyboardAndleAdjustInterval) {
                clearInterval(this.keyboardAndleAdjustInterval)
            }
            this.targetKeyboardAngle = newTargetAngle

            const increment = 5

            const rotator = new DegreesRotator(
                Math.floor(radians_to_degrees(newTargetAngle)),
                increment,
                Math.floor(radians_to_degrees(this.entity.movement.angle)),
            )

            this.keyboardAndleAdjustInterval = setInterval(() => {
                const done = rotator.update()
                this.entity.movement.angle = degrees_to_radians(rotator.normalizedCurrent())
                if (done) {
                    this.entity.movement.angle = degrees_to_radians(rotator.normalizedTarget())
                    clearInterval(this.keyboardAndleAdjustInterval)
                }
            }, 5)
        }
    }

    updateRoom = () => {
        // A pick armed in the room just left has nothing to point at any more.
        this.cancelLocationPick()
        this.clearSharedBuilds()
        this.clearPendingBuilds()
        this.buildPreviewRevision++
        this.buildPreview = undefined
        this.clearTerrainPointer()
        this.buildPointer = undefined
        this.placementPointer = undefined
        this.placementPointerPose = undefined

        if (this.pointer) {
            this.pointer = undefined
        }

        if (this.roughPointer) {
            this.roughPointer = undefined
        }

        this.hoveredEntity = undefined
    }

    updateOrientation = (orientation: Orientation) => {
        const { entity } = this

        switch (orientation) {
            case "south": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: 0, y: -1 })
                break
            }
            case "north": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: 0, y: 1 })
                break
            }
            case "east": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: -1, y: 0 })
                break
            }
            case "west": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: 1, y: 0 })
                break
            }
        }
    }

    /**
     * Open chat, asked for by something other than a key -- the SAY chip in a camp's HUD row, or
     * the chat button on the control bar.
     *
     * It picks the field the device can actually use. A phone has no Enter to open the inline field
     * with, no Escape to leave it with, and a soft keyboard does not produce the keystrokes that
     * field reads, so touch gets the composer: a real DOM <input>, which is the only thing that
     * raises the keyboard at all. A desktop gets exactly what Enter gives it, `isChat` included, so
     * a line begun with the mouse can be sent with the keyboard.
     */
    requestChat = () => {
        // The tap that asked for chat is still in flight: a chip reports a `click`/`tap` on pointer
        // UP, while the world's attack arrives on a document-level `mousedown` that cannot see the
        // widget it landed on. Without this, opening chat swings whatever is in the player's hand.
        this.suppressAttack()
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        if (isMobile()) {
            if (gui.isChatComposerVisible()) {
                return
            }
            // A stick still under a thumb when the field takes the touch would stay latched: the
            // joystick's onChange only fires on pointermove, so nothing would ever clear it and the
            // player would type while walking off into the dark.
            this.setJoystickIntent(null)
            gui.openChatComposer()
            return
        }
        if (this.isChat) {
            return
        }
        this.client.startChat()
        this.isChat = true
    }

    // Pasted text only means anything while the chat input is open; anywhere else in the game
    // there is nothing it could be pasted INTO.
    private respondToPaste = (text: string): void => {
        if (!this.isChat || !this.client.clientGameLogic.isBootstrapped()) {
            return
        }
        this.client.pasteIntoChat(text)
    }

    private respondToKeyBoard = (key: string, down: boolean, repeat?: boolean, mods?: KeyModifiers): void => {
        if (key === "Shift" && !down) this.buildingShapePaletteDismissed = false
        if (!this.client.clientGameLogic.isBootstrapped()) {
            // can't do anything unless you're bootstrapped
            return
        }
        if (!this.keyboardActionEnabled) {
            return
        }
        const { entity } = this

        if (key === " " && (entity.weapon?.profileId || this.localWeaponCharge) && !this.terrainPointerOn) {
            this.setAttackHeld(down)
            return
        }

        if (this.isKeyOn("/") && !this.isChat) {
            this.client.startChat()
            this.isChat = true
        }

        if (this.isKeyOn("Enter")) {
            if (this.isChat) {
                this.client.stopChat()
            } else {
                this.client.startChat()
            }

            this.isChat = !this.isChat
        }

        if (this.isChat && down) {
            this.client.captureKeystroke(key, mods)
        }

        if (this.isKeyOn("Escape") && this.isChat) {
            this.client.stopChat()
            this.isChat = false
        }

        if (this.isChat) {
            return
        }

        // Handle Control key for command wheel
        if (key === "Control" || key === "ControlLeft" || key === "ControlRight") {
            if (down) {
                // Control key pressed - show command wheel
                this.client.clientGameLogic.clientRenderer.gui().showCommandWheel()
            } else {
                // Control key released - hide command wheel
                this.client.clientGameLogic.clientRenderer.gui().hideCommandWheel()
            }
        }

        // Escape dismisses the per-entity command wheel (opened from an NPC's command button,
        // so it isn't tied to holding Control).
        if ((key === "Escape" || key === "Esc") && down) {
            const gui = this.client.clientGameLogic.clientRenderer.gui()
            if (gui.isCommandWheelVisible()) {
                gui.hideCommandWheel()
            }
            // Also dismiss the recruit role picker if it's open.
            if (gui.isRolePickerVisible()) {
                gui.hideRolePicker()
            }
            // ...and either possession splash, which is only ever telling the player something, so
            // Escape is simply the keyboard's dismiss button.
            if (gui.isPossessionSplashVisible()) {
                gui.hidePossessionSplash()
            }
            // ...and the invasion picker, which is open over the end-of-run scorecard while the
            // player is still DOWNED -- so this has to sit above the paralyzed/downed guard below,
            // where it already does. Routed through cancelInvasionPicker so the keyboard way out
            // tells the server exactly what the footer button and the scrim do.
            if (gui.isInvasionPickerVisible()) {
                gui.cancelInvasionPicker()
            }
            // ...and the name prompt. Escape is the one key that still reaches here while a text
            // field holds the keyboard (Keyboard.suppressTyping), precisely so that a field whose
            // focus has been clicked away can still be dismissed.
            if (gui.isNamePromptVisible()) {
                gui.hideNamePrompt()
            }
            // ...and the chat composer, for the same reason: its own Escape arrives only while its
            // field has focus, and a click on the world takes that away.
            if (gui.isChatComposerVisible?.()) {
                gui.dismissChatComposer()
            }
            // Escape cancels printing without changing the equipped building material.
            if (this.terrainPointerOn || this.pendingBuilds.size) this.cancelPendingPrinting()
            // Escape backs out of an armed location pick (the defender's "Hold Position") the same
            // way it backs out of build mode: the order is simply never sent.
            this.cancelLocationPick()
            // Also deactivate the ember follow-cam PiP / roster slot.
            gui.dismissFollowCam()
        }

        if (this.paralyzed || this.downed) {
            entity.movement.moving = false
            entity.speed2.x = 0
            entity.speed2.y = 0
            return
        }

        if (Date.now() - this.lastMouseMoveTs > 150) {
            this.keyboardBasedAngle(entity)
        }

        const isMoving =
            this.isKeyOn("w") ||
            this.isKeyOn("ArrowUp") ||
            this.isKeyOn("s") ||
            this.isKeyOn("ArrowDown") ||
            this.isKeyOn("a") ||
            this.isKeyOn("ArrowLeft") ||
            this.isKeyOn("d") ||
            this.isKeyOn("ArrowRight")

        let manualControl = isMoving
        if (isMoving) {
            clearInterval(this.autoMovingInterval)
            this.setAutoMoving(false)
        }

        // Remove immediate velocity zeroing to allow inertial slowdown
        // The updatePlayerVelocity method will handle velocity updates with inertia

        if (manualControl) {
            const wasMoving = entity.movement.moving

            entity.movement.moving = isMoving

            if (!wasMoving && entity.movement.moving) {
                // started moving!
                this.movementStartTime = Date.now()
                this.isSustainedMovement = false
                if (this.movementDaemon !== undefined) {
                    clearInterval(this.movementDaemon)
                }
                this.movementDaemon = setInterval(() => {
                    if (Date.now() - this.lastMouseMoveTs > 150) {
                        this.keyboardBasedAngle(entity)
                    }
                }, 150)
            }
            if (wasMoving && !entity.movement.moving) {
                // stopped moving
                this.movementStartTime = 0
                this.isSustainedMovement = false
                if (this.movementDaemon !== undefined) {
                    clearInterval(this.movementDaemon)
                }
            }

            if (!entity.afk) {
                // Set target speed2 values
                this.targetSpeed2 = { x: 0, y: 0 }

                if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                    this.targetSpeed2.y = -1
                }
                if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                    this.targetSpeed2.y = 1
                }
                if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                    this.targetSpeed2.x = -1
                }
                if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                    this.targetSpeed2.x = 1
                }
            } else {
                // If AFK, set target to zero
                this.targetSpeed2 = { x: 0, y: 0 }
            }
        }

        if (this.isKeyOn("e")) {
            if (this.terrainPointerOn) {
                this.client.clientGameLogic.activateWorldPoint(this.pointer, {
                    delete: true,
                })
            }
        }

        if (this.isKeyOn("p")) {
            this.updateTerrainPointerOn(!this.terrainPointerOn)
        }

        if (this.isKeyOn("u")) {
            this.client.spawnNpc()
        }

        if (this.isKeyOn("x")) {
            this.client.dropAction()
        }

        if (this.isKeyOn("y")) {
            this.client.itemPickupAction()
        }

        if (this.isKeyOn("b")) {
            this.activeAbility("b")
        }

        if (this.isKeyOn(" ") && (!this.terrainPointerOn || (key === " " && down && !repeat))) {
            this.manualAttack(isMoving)
        }

        if (this.isKeyOn("q")) {
            this.client.dropActiveItem()
        }

        if (this.isKeyOn("]")) {
            this.client.activateAbility("e")
        }

        if (this.isKeyOn("1")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(1)
        }

        if (this.isKeyOn("2")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(2)
        }

        if (this.isKeyOn("3")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(3)
        }

        if (this.isKeyOn("4")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(4)
        }

        if (this.isKeyOn("5")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(5)
        }

        if (this.isKeyOn("6")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(6)
        }

        if (this.isKeyOn("j")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "come",
                },
            })
        }

        if (this.isKeyOn("h")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "stopStart",
                },
            })
        }

        if (this.isKeyOn("k")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "formation",
                },
            })
        }

        if (this.isKeyOn("l")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "updateStance",
                },
            })
        }

        if (key === "Shift") {
            if (down) {
                this.client.boost(true)
                // The run hint is NOT dismissed here. Pressing Shift is not running, and Shift also
                // opens the building shape palette, so this took the hint off screen for a player
                // laying a wall who had never run a step -- and for anyone whose hand brushed the
                // key while reading it. Earned in accrueRunHint instead, once the body has actually
                // moved under the key.
            } else {
                this.client.boost(false)
            }
        }

        if (this.isKeyOn("~")) {
            if (this.randomness) {
                clearInterval(this.randomness)
                this.randomness = null
            } else {
                const latch = new Latch(500)
                this.randomness = setInterval(() => {
                    if (latch.expired()) {
                        const w = randomIntBetween(0, 20)
                        this.client.fireBullet()
                    }
                    const r = randomIntBetween(0, 1)
                    entity.movement.moving = true
                    entity.speed2.x = r * (randomIntBetween(0, 1) === 1 ? -1 : 1)
                    entity.speed2.y = r * (randomIntBetween(0, 1) === 1 ? -1 : 1)
                }, 100)
            }
        }

        if (this.isKeyOn("i")) {
            this.client.clientGameLogic.clientRenderer.gui().toggleInventory()
            // this.client.clientGameLogic.invokeInventory()
        }

        // Toggle the frame-rate readout. Gated on the key's own key-down, and on it not being an
        // auto-repeat, so holding `f` doesn't flip it open and shut.
        //
        // This key used to toggle the offscreen faction arrows. Those are now simply on wherever a
        // room asks for them (an ember camp), which is what players want of them -- they point at
        // your own camp and the people in it, and there is no moment where knowing less about where
        // they are is an improvement.
        if ((key === "f" || key === "F") && down && !repeat && !this.isChat) {
            this.client.clientGameLogic.clientRenderer.gui().toggleFpsMeter()
        }

        // Toggle the crafting screen. Gated on the key's own key-down, and on it not being an
        // auto-repeat, so holding `c` doesn't flip the screen open and shut.
        if ((key === "c" || key === "C") && down && !repeat) {
            this.client.clientGameLogic.clientRenderer.gui().toggleCrafting()
        }

        if (this.isKeyOn("t")) {
            if (entity.debug || entity.name === "aron") {
                this.client.clientGameLogic.clientRenderer.gui().toggleCharacterCustomizer()
            }
            // this.client.clientGameLogic.clientRenderer.gui().showTutorial("hotbar")
        }
    }

    perturb = () => {
        const { entity } = this
        entity.speed2.x = 0.001
        setTimeout(() => {
            entity.speed2.x = 0
        }, 10)
    }

    private respondToMouse = (mouseState: MouseState): void => {
        // Mobile building is driven by the touch owner, not emulated mouse events.
        if (isMobile() && this.terrainPointerOn) return
        const mouseMoved = !this.lastPlacementMousePosition || !isEqual(this.lastPlacementMousePosition, mouseState.position)
        this.lastPlacementMousePosition = { ...mouseState.position }
        if (!mouseState.buttons.get("0") && this.mouseAttackHeld) {
            this.stopFiring(true)
            this.releasePendingBuilds()
        }
        if (!this.mouseActionEnabled) {
            return
        }
        this.lastMouseMoveTs = Date.now()
        if (!isMobile()) {
            if (!this.pointer && this.client.clientGameLogic.tilemapMetaProvider) {
                const pointer = {
                    x: 64,
                    y: 64,
                }
                this.pointer = pointer
            }
            if (!this.roughPointer && this.client.clientGameLogic.tilemapMetaProvider) {
                const pointer = {
                    x: 0,
                    y: 0,
                }
                this.roughPointer = pointer
            }
            this.client.clientGameLogic.clientRenderer.entity()
            const X_FACTOR = ScalingParams.viewportX
            const Y_FACTOR = ScalingParams.viewportY - 1
            const entity = this.client.clientGameLogic.playerEntity
            const tilemapMetaProvider = this.client.clientGameLogic.tilemapMetaProvider
            if (tilemapMetaProvider) {
                const location = entity.location
                const rightThreshold = (tilemapMetaProvider.width() - X_FACTOR) * TileSize
                const bottomThreshold = (tilemapMetaProvider.height() - Y_FACTOR) * TileSize

                const isOverX = location.x > TileSize * X_FACTOR && location.x > rightThreshold
                const isOverY = location.y > TileSize * Y_FACTOR && location.y > bottomThreshold

                if (this.pointer) {
                    const renderer: MapRenderingManager =
                        this.client.clientGameLogic.clientRenderer.map() as MapRenderingManager
                    renderer.updateCompositeTilePointer?.(mouseState.position)
                    const { modifierX, modifierY } = renderer.translationComputer
                    const factor = 0.5
                    const yfactor = 0.38
                    this.pointer = {
                        x:
                            Math.round(mouseState.position.x * factor) +
                            Math.round((modifierX !== undefined ? -modifierX : 0) * 1) -
                            (modifierX !== undefined
                                ? Math.round(TileSize * X_FACTOR * factor)
                                : isOverX
                                ? -rightThreshold + TileSize * X_FACTOR
                                : 0) -
                            (modifierX !== undefined ? 40 : -24),
                        y:
                            Math.round(mouseState.position.y * yfactor) +
                            Math.round((modifierY !== undefined ? -modifierY : 0) * 1) -
                            (modifierY !== undefined
                                ? Math.round(TileSize * Y_FACTOR * yfactor)
                                : isOverY
                                ? -bottomThreshold + TileSize * Y_FACTOR
                                : 0) -
                            (modifierY !== undefined ? 0 : -30),
                    }
                    this.pointer.y += -10
                    const newRoughPointerX = Math.floor(this.pointer.x / TileSize)
                    const newRoughPointerY = Math.floor(this.pointer.y / TileSize)
                    if (this.roughPointer.x !== newRoughPointerX || this.roughPointer.y !== newRoughPointerY) {
                        this.roughPointer.x = newRoughPointerX
                        this.roughPointer.y = newRoughPointerY
                    }

                    if (!this.placementItem()) {
                        this.entity.movement.angle = getAngle(this.pointer, entity.location)
                        this.targetKeyboardAngle = this.entity.movement.angle
                    } else if (mouseMoved && !this.locationPick) {
                        this.refreshBuildPointer()
                        const origin = fineToRoughCoordinates(entity.location)
                        const rule = Mechanics.item.properties.placement(this.placementItem().itemTypeMeta)
                        this.placementPointer = clampPlacementTarget(origin, this.roughPointer, rule.range)
                        if (!isEqual(this.placementPointer, origin)) {
                            clearInterval(this.keyboardAndleAdjustInterval)
                            entity.movement.angle = getAngle(roughToFineCoordinatesOffset(this.placementPointer), entity.location)
                            this.targetKeyboardAngle = entity.movement.angle
                        }
                        // Mouse aiming owns this target; only a subsequent move or turn resets it.
                        this.placementPointerPose = { ...entity.location, angle: entity.movement.angle }
                    }
                    // Keep the building preview on the grid cell under the mouse.
                    this.updatedRoughPointer(this.roughPointer)

                    // Keep an armed location pick's verdict current as the pointer travels: the
                    // reticle is amber over ground the NPC could stand on, red over ground it
                    // could not.
                    if (this.locationPick) {
                        this.client.clientGameLogic.clientRenderer
                            .gui()
                            .setLocationPickerBlocked(!this.locationPickable())
                    }

                    // ... and not the bow's crosshair on top of it: while a pick is armed the
                    // location reticle IS the pointer, and two of them read as a bug.
                    if (this.longRangedEquipped && !this.terrainPointerOn && !this.locationPick) {
                        const mouseSprite = this.client.clientGameLogic.clientRenderer.gui().createCursorSprite()
                        mouseSprite.visible = true
                        mouseSprite.alpha = 1.0
                        if (this.mousePointerFadeInterval) {
                            clearInterval(this.mousePointerFadeInterval)
                        }
                        if (this.mousePointerFader) {
                            this.mousePointerFader.stop(true)
                            this.mousePointerFader = null
                        }
                        this.mousePointerFadeInterval = setTimeout(() => {
                            this.mousePointerFader = new Fader(mouseSprite, {
                                releaseCallback: () => {
                                    mouseSprite.visible = false
                                },
                            })
                            this.mousePointerFader.update()
                        }, 500)
                    }
                }
            }

            if (!this.placementItem()) clearInterval(this.keyboardAndleAdjustInterval)
        }

        if (this.isButtonOn("0")) {
            // A click on an interaction icon (chest open/close, chat, etc.) must never also
            // trigger a world attack. The attack click arrives via a document-level mousedown
            // that can't tell it landed on a pixi interaction widget, so we suppress attacks
            // for a short window around every interaction activation (see suppressAttack).
            if (Date.now() < this.suppressAttackUntilTs) {
                return
            }

            if (this.client.clientGameLogic.clientRenderer.gui().isBuildingPrintControlHovered?.()) return

            // Check if command wheel is visible - if so, don't process mouse clicks for attacks
            if (this.client.clientGameLogic.clientRenderer.gui().isCommandWheelVisible()) {
                return
            }

            // Same for the name prompt: it stands open for as long as it takes to type a name, with
            // the player's attention on a field rather than the world, so a click meant for it (or
            // for the field it just lost focus to) must not fire a shot.
            if (this.client.clientGameLogic.clientRenderer.gui().isNamePromptVisible?.()) {
                return
            }

            // Same for the chat composer. Its panel swallows the pixi events over itself, but the
            // attack arrives on a document-level mousedown that cannot see what it landed on -- so
            // without this, tapping Send also swings whatever is in the player's hand.
            if (this.client.clientGameLogic.clientRenderer.gui().isChatComposerVisible?.()) {
                return
            }

            // Likewise, a click on an inventory/crafting/trading screen (e.g. an item action
            // button) must not also fire a world attack. Those modals draw over the world but the
            // attack arrives via a document-level mousedown that can't see the pixi widget it hit,
            // so gate on the open screen the same way the command wheel does above.
            if (this.client.clientGameLogic.clientRenderer.gui().isItemScreenOpen()) {
                return
            }

            // button pressed
            if (!this.isFiring) {
                this.isFiring = true
                this.mouseAttackHeld = true

                // An armed location pick claims this press: it is the player answering "click a
                // spot", not an attack on whatever happens to be under the reticle.
                if (this.locationPick) {
                    this.completeLocationPick()
                    return
                }

                // An entity touch already owns this press.
                if (this.pressedEntityId) {
                    return
                }

                if (this.terrainPointerOn) {
                    this.startWorldAction({ ignoreHover: true })
                } else {
                    this.isFiring = false
                    this.setAttackHeld(true)
                    this.mouseAttackHeld = this.isFiring
                }
            }
        }
    }

    // Primary presses attack, place build tiles, or resolve a chosen ground location.
    // ignoreHover keeps friendly interactable bodies from intercepting a combat swing.
    private startWorldAction = (opts?: { ignoreHover?: boolean }) => {
        if (!this.terrainPointerOn && !this.locationPick &&
            this.interactionsFor(this.hoveredEntity).some(i => i.holdDurationMs > 0)) return
        if (this.terrainPointerOn) {
            const rectangle = isMobile() ? this.mobileBuildTool === "line" || this.mobileBuildTool === "room" : this.isKeyOn("Shift")
            this.refreshBuildPointer()
            const initialKey = `${this.buildPointer.x},${this.buildPointer.y}`
            const alreadyPending = this.pendingBuilds.has(initialKey)
            this.placeBuildTile(!rectangle, true)
            const visited = new Set([`${this.buildPointer.x},${this.buildPointer.y}`])
            const item = this.entity.inventory?.items?.find(i => i.active && Mechanics.item.properties.buildable(i.itemTypeMeta))
            const buildType = item && Mechanics.item.properties.buildType(item.itemTypeMeta)
            if (buildType && ["wall", "roof", "floor", "floor2"].includes(buildType)) {
                this.buildStroke = {
                    last: { ...this.buildPointer }, visited,
                    area: rectangle ? {
                        anchor: { ...this.buildPointer },
                        cells: new Set(!alreadyPending && this.pendingBuilds.has(initialKey) ? [initialKey] : []),
                        hollow: buildType === "wall",
                        shape: isMobile() ? this.mobileBuildTool === "line" ? "line" : "rectangle" : this.buildingShape,
                    } : undefined,
                }
                if (rectangle) {
                    this.buildingShapePaletteDismissed = true
                    this.updateBuildingShapePalette()
                }
                return
            }
            this.fireInterval = setInterval(() => {
                this.refreshBuildPointer()
                const key = `${this.buildPointer.x},${this.buildPointer.y}`
                if (visited.has(key)) return
                visited.add(key)
                this.placeBuildTile(false, true)
            }, 400)

            this.lastMouseMoveTs = Date.now()
            return
        }

        const hoveredEntity = opts?.ignoreHover
            ? undefined
            : this.client.clientGameLogic.getEntity(this.hoveredEntity)
        let isAutoTravelling = false

        const entityTilePointer = !emptyCoordinate(this.roughCompositeTilePointer)
            ? this.roughCompositeTilePointer
            : this.roughPointer

        const entityTile = this.client.clientGameLogic.tilemapMetaProvider.getEntityTileAt(
            entityTilePointer.x,
            entityTilePointer.y,
        )
        // Close enough to swing at. Measured to the MIDDLE of the cell, not its top-left corner:
        // `roughToFineCoordinates` hands back the corner, so the set of places you were allowed to
        // strike from was a disc around that corner -- you could cut a clump of grass standing on it,
        // from its left or from above, and not from below or from the right. Below is how a player
        // walks up to anything, the camera looking down, so the usual approach was the one that did
        // nothing: no strike, no message, the click swallowed by the walk-and-shoot fallback.
        //
        // One and a half tiles from the centre, so the whole eight-cell ring around a plant counts --
        // 32px orthogonally, 45px on the diagonal -- which is what "an adjacent entity tile" meant
        // all along.
        const entityTileAccessible =
            entityTile &&
            getCoordinateDistance(this.entity.location, roughToFineCoordinatesOffset(entityTilePointer)) <
                TileSize * 1.5

        if (!entityTileAccessible && !this.longRangedEquipped) {
            // Say WHY, not just that. There are three separate reasons a click on something that
            // plainly looks strikeable sends nothing -- no composite was hovered, the cell holds no
            // entity tile, or the player is out of the one-tile reach -- and from the outside all
            // three look identical: you swing and the world ignores you.
            logger.info(
                { tags: ["callback", "animation"] },
                `Mouse click: Entity tile not accessible, not long-ranged. hoveredEntity=${!!hoveredEntity}` +
                    ` composite=${emptyCoordinate(this.roughCompositeTilePointer) ? "none" : `${this.roughCompositeTilePointer.x},${this.roughCompositeTilePointer.y}`}` +
                    ` pointer=${this.roughPointer?.x},${this.roughPointer?.y}` +
                    ` aimedAt=${entityTilePointer.x},${entityTilePointer.y}` +
                    ` tile=${entityTile?.entityTile ?? "none"}` +
                    ` reach=${Math.round(getCoordinateDistance(this.entity.location, roughToFineCoordinatesOffset(entityTilePointer)))}/${TileSize * 1.5}`,
            )

            if (hoveredEntity) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `Mouse click: Scheduling auto-travel to hovered entity ${hoveredEntity.entityId}`,
                )
                isAutoTravelling = this.scheduleAutoTravelToEntity(hoveredEntity, () => {
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Firing bullet after reaching hovered entity`,
                    )
                    this.client.fireBullet()
                })
            } else {
                const collisionCheck = calculateNewCoordinateFromRadians(
                    this.entity.location,
                    this.entity.movement.angle,
                    TileSize,
                )
                if (
                    this.client.clientGameLogic.tilemapMetaProvider.isCollisionAt(
                        fineToRough(collisionCheck.x),
                        fineToRough(collisionCheck.y),
                    )
                ) {
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Collision detected, no auto-travel`,
                    )
                    isAutoTravelling = false
                } else {
                    const thePoint = calculatePointAtDistance(
                        this.entity.location,
                        this.pointer,
                        TileSize * 1.5,
                    )
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Scheduling auto-travel to calculated point (${thePoint.x}, ${thePoint.y})`,
                    )
                    isAutoTravelling = this.scheduleAutoTravelToPoint(
                        thePoint,
                        () => {
                            logger.info(
                                { tags: ["callback", "animation"] },
                                `Mouse click: Firing bullet after reaching calculated point`,
                            )
                            this.client.fireBullet()
                        },
                        false,
                    )
                }
            }
        }

        if (!isAutoTravelling) {
            if (entityTileAccessible && !hoveredEntity) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `Mouse click: Entity tile accessible, doing strike attack`,
                )
                const doStrike = () => {
                    const s = { ...this.roughCompositeTilePointer }
                    this.client.clientGameLogic.attack(this.entity.entityId, () => {
                        this.client.entityTileStrike(s)
                    })
                }

                doStrike()
                this.fireInterval = setInterval(() => {
                    doStrike()
                }, 400)
            } else {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `Mouse click: No auto-travel, no accessible entity tile - firing bullet (no automatic boost - use Shift key for boost)`,
                )
                this.client.fireBullet()

                this.fireInterval = setInterval(() => {
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Repeating bullet fire in interval (no automatic boost)`,
                    )
                    this.client.fireBullet()
                }, 400)
            }
        }

        this.lastMouseMoveTs = Date.now()
    }

    // Stop an in-flight press: clears the repeat so a held attack can't outlive the press. Shared by
    // the mouse-up path and the entity press handlers, because a touch that is held never produces
    // the emulated mouseup a desktop release does.
    private stopFiring = (releaseCharge = false) => {
        const charge = this.localWeaponCharge
        this.localWeaponCharge = undefined
        if (charge) {
            const valid = releaseCharge && this.canHoldAttack() && this.entity.weapon?.profileId === charge.profileId &&
                this.entity.inventory?.items.some(item => item.id === charge.itemId && item.active && item.quantity > 0)
            const origin = this.entity.getBounds().center()
            const target = this.mouseAttackHeld && this.pointer ? this.pointer : {
                x: origin.x + Math.cos(this.entity.movement.angle) * (this.entity.chargedWeaponDefinition?.maxRange || TileSize * 6),
                y: origin.y + Math.sin(this.entity.movement.angle) * (this.entity.chargedWeaponDefinition?.maxRange || TileSize * 6),
            }
            this.client.chargedWeaponAction(valid ? "release" : "cancel", charge.id, target)
        }
        this.buildStroke = undefined
        this.isFiring = false
        this.mouseAttackHeld = false
        clearInterval(this.fireInterval)
        this.fireInterval = undefined
    }

    isAttacking = (): boolean => this.isFiring

    private interactionsFor = (entityId?: EntityId): InteractionDescriptor[] => {
        if (!entityId) {
            return []
        }
        return this.client.clientGameLogic.clientRenderer.entity().getEntityInteractions(entityId) || []
    }

    private heldInteraction?: { entityId: EntityId; type: InteractionType; timer: ReturnType<typeof setInterval> }

    private cancelHeldInteraction = () => {
        const held = this.heldInteraction
        if (!held) return
        clearInterval(held.timer)
        this.client.clientGameLogic.interactionCallback(held.entityId, held.type, "cancel")
        this.heldInteraction = undefined
    }

    // Mouse primary clicks attack immediately. Secondary clicks open menus; touch
    // holds open menus and short touches attack when released.
    beginTilePress = (cell: Coordinate, pointerType: string, button = 0): boolean => {
        const logic = this.client.clientGameLogic
        const gui = logic.clientRenderer.gui()
        if ((pointerType !== "touch" && button !== 2) || this.locationPick || this.pressedEntityId ||
            this.paralyzed || this.downed || this.entity.isDead() ||
            gui.isCommandWheelVisible() ||
            gui.isItemScreenOpen() || gui.isRolePickerVisible?.() || gui.isChatComposerVisible?.() ||
            gui.isNamePromptVisible?.()) return false
        const target = tileInteractionsAt(logic.tilemapMetaProvider, cell)
        if (!target) return false
        this.cancelTilePress()
        this.pressedTile = { ...cell }
        this.stopFiring()
        this.suppressAttack(1000)
        const open = () => {
            this.tilePressTimer = undefined
            gui.showTileInteractionWheel?.(target)
        }
        if (pointerType === "touch") this.tilePressTimer = setTimeout(open, 500)
        else open()
        return true
    }

    cancelTilePress = () => {
        if (this.tilePressTimer) clearTimeout(this.tilePressTimer)
        this.tilePressTimer = undefined
        this.pressedTile = undefined
    }

    moveTilePress = (cell: Coordinate) => {
        if (this.pressedTile && (cell.x !== this.pressedTile.x || cell.y !== this.pressedTile.y)) this.cancelTilePress()
    }

    beginEntityPress = (entityId: EntityId, pointerType = "touch", button = 0) => {
        const held = this.interactionsFor(entityId).find(i => i.holdDurationMs > 0)
        if (pointerType !== "touch" && button !== 2 && !held) {
            return
        }
        if (this.interactionsFor(entityId).length === 0) {
            return
        }
        // Build mode owns the press: placing a tile on the ground an interactable stands on must not
        // open the interactable instead.
        if (this.terrainPointerOn) {
            return
        }
        // Same for an armed location pick: "hold THIS spot" has to stay available even when an NPC
        // happens to be standing on the spot being chosen.
        if (this.locationPick) {
            return
        }
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        // A wheel or picker already open sits directly over its entity, so a press on one of its
        // slices lands on the entity underneath too. The open widget owns that press.
        if (
            gui.isCommandWheelVisible() ||
            gui.isRolePickerVisible?.() ||
            gui.isNamePromptVisible?.() ||
            gui.isChatComposerVisible?.() ||
            gui.isItemScreenOpen()
        ) {
            return
        }
        if (held && (pointerType === "touch" || button === 0)) {
            this.cancelHeldInteraction()
            this.stopFiring()
            this.suppressAttack(held.holdDurationMs + 1000)
            this.pressedEntityId = entityId
            this.client.clientGameLogic.interactionCallback(entityId, held.type, "begin")
            this.heldInteraction = { entityId, type: held.type, timer: setInterval(() => {
                this.suppressAttack(1000)
                this.client.clientGameLogic.interactionCallback(entityId, held.type, "refresh")
            }, 250) }
            return
        }
        if (pointerType !== "touch") {
            this.openEntityContextMenu(entityId)
            return
        }
        this.clearPressHoldTimer()
        this.pressedEntityId = entityId
        this.pressOpenedMenu = false
        this.stopFiring()
        this.suppressAttack(800)
        this.pressHoldTimer = setTimeout(() => {
            this.pressHoldTimer = undefined
            this.pressOpenedMenu = true
            this.openEntityContextMenu(entityId)
        }, ENTITY_PRESS_HOLD_MS)
    }

    endEntityPress = (entityId: EntityId) => {
        if (this.pressedEntityId !== entityId) {
            return
        }
        const consumed = this.pressOpenedMenu || !!this.heldInteraction
        this.releaseEntityPress()
        this.suppressAttack()
        if (!consumed) {
            this.startWorldAction({ ignoreHover: true })
            this.stopFiring()
        }
    }

    cancelEntityPress = (entityId: EntityId) => {
        if (this.pressedEntityId !== entityId) {
            return
        }
        const openedMenu = this.pressOpenedMenu
        this.releaseEntityPress()
        if (openedMenu) {
            this.stopFiring()
        }
    }

    private releaseEntityPress = () => {
        this.cancelHeldInteraction()
        this.clearPressHoldTimer()
        this.pressedEntityId = undefined
        this.pressOpenedMenu = false
    }

    private clearPressHoldTimer = () => {
        if (this.pressHoldTimer) {
            clearTimeout(this.pressHoldTimer)
            this.pressHoldTimer = undefined
        }
    }

    private cancelHeldAttack = () => {
        this.cancelTilePress()
        this.releaseEntityPress()
        this.stopFiring()
        this.releasePendingBuilds()
    }

    private canHoldAttack = (): boolean => {
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        return !this.heldInteraction && !this.interactionsFor(this.hoveredEntity).some(i => i.holdDurationMs > 0) &&
            !this.isChat && !this.paralyzed && !this.downed && !this.entity.isDead() &&
            !this.locationPick && Date.now() >= this.suppressAttackUntilTs &&
            !gui.isCommandWheelVisible() && !gui.isItemScreenOpen() &&
            !gui.isRolePickerVisible?.() && !gui.isNamePromptVisible?.() && !gui.isChatComposerVisible?.()
    }

    /** Mouse and mobile holds repeat the same action as holding Space. */
    setAttackHeld = (held: boolean) => {
        if (!held) {
            this.stopFiring(true)
            return
        }
        if (this.isFiring || !this.canHoldAttack()) return
        this.isFiring = true
        if (this.entity.weapon?.profileId) {
            const definition = this.entity.chargedWeaponDefinition
            const item = this.entity.inventory?.items.find(item => item.active && item.quantity > 0 &&
                item.flags?.chargedWeaponId === this.entity.weapon.profileId)
            if (!definition || !item) { this.stopFiring(); return }
            const id = randomId()
            this.localWeaponCharge = { id, profileId: definition.id, itemId: item.id }
            this.client.chargedWeaponAction("begin", id)
            this.fireInterval = setInterval(() => {
                if (!this.canHoldAttack() || this.entity.weapon?.profileId !== definition.id ||
                    !this.entity.inventory?.items.some(i => i.id === item.id && i.active && i.quantity > 0)) this.stopFiring()
            }, HELD_ATTACK_MS)
            return
        }
        const attack = () => {
            if (!this.canHoldAttack()) {
                this.stopFiring()
                return
            }
            this.manualAttack(!!this.entity.movement?.moving)
        }
        attack()
        if (this.isFiring) this.fireInterval = setInterval(attack, HELD_ATTACK_MS)
    }

    openEntityContextMenu = (entityId: EntityId) => {
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        if (this.terrainPointerOn || this.locationPick || gui.isCommandWheelVisible() ||
            gui.isItemScreenOpen() || gui.isChatComposerVisible?.() ||
            gui.isRolePickerVisible?.() || gui.isNamePromptVisible?.()) return
        // Held verbs are left out: a wheel has no way to hold a slice down, so one would be a dead
        // button (see wheelInteractions). An entity offering nothing BUT a held verb therefore has
        // no menu at all -- a secondary click on an injured survivor opens nothing, because holding
        // on her IS the interaction and a one-slice wheel saying so would do nothing when picked.
        const interactions = wheelInteractions(this.interactionsFor(entityId))
        if (!interactions.length) {
            return
        }
        this.stopFiring()
        this.suppressAttack()
        this.client.clientGameLogic.clientRenderer.gui().showInteractionWheel(entityId, interactions)
    }

    updatedRoughPointer = (coordinate: Coordinate) => {
        this.roughPointer = { ...coordinate }
        const stroke = this.buildStroke
        if (stroke && this.isFiring && this.terrainPointerOn) {
            const gui = this.client.clientGameLogic.clientRenderer.gui()
            const blocked = (Date.now() < this.suppressAttackUntilTs && !this.mobileBuildDragging) || gui.isBuildingPrintControlHovered?.() ||
                gui.isItemScreenOpen() || gui.isCommandWheelVisible() || gui.isChatComposerVisible?.() || gui.isNamePromptVisible?.()
            if (!blocked) {
                let changed = false
                if (stroke.area) {
                    changed = this.updateBuildArea(coordinate, stroke.area)
                } else {
                    const dx = coordinate.x - stroke.last.x
                    const dy = coordinate.y - stroke.last.y
                    const nx = Math.abs(dx), ny = Math.abs(dy)
                    let x = stroke.last.x, y = stroke.last.y, ix = 0, iy = 0
                    // Cross grid boundaries in order. On diagonal ties, step one axis at a time
                    // so wall strokes remain cardinally connected rather than touching at corners.
                    while (ix < nx || iy < ny) {
                        if (ix < nx && (iy >= ny || (1 + 2 * ix) * ny <= (1 + 2 * iy) * nx)) {
                            x += Math.sign(dx)
                            ix++
                        } else {
                            y += Math.sign(dy)
                            iy++
                        }
                        const key = `${x},${y}`
                        if (stroke.visited.has(key)) continue
                        stroke.visited.add(key)
                        this.placeBuildTile(false, true, { x, y }, false)
                        changed = true
                    }
                }
                if (changed) {
                    this.playBuildSwing()
                    this.clearTerrainPointer()
                    this.refreshPendingPreviews()
                    this.refreshBuildPointer(true)
                }
            }
            stroke.last = { ...coordinate }
        }
        this.refreshBuildPointer()
    }

    private updateBuildArea = (coordinate: Coordinate, area: { anchor: Coordinate; cells: Set<string>; hollow: boolean; shape: BuildingShape }): boolean => {
        const map = this.client.clientGameLogic.tilemapMetaProvider
        const points = buildingShapeCells(area.anchor, coordinate, area.shape, area.hollow, map.width(), map.height())
        const desired = new Set(points.map(point => `${point.x},${point.y}`))
        let changed = false
        area.cells.forEach(key => {
            if (!desired.has(key)) {
                this.removePendingBuild(key, false)
                area.cells.delete(key)
                changed = true
            }
        })
        for (const point of points) {
            const key = `${point.x},${point.y}`
            if (this.pendingBuilds.has(key)) continue
            if (this.pendingBuilds.size >= 256) break
            this.placeBuildTile(false, true, point, false)
            if (this.pendingBuilds.has(key)) area.cells.add(key)
            changed = true
        }
        return changed
    }

    selectBuildingShape = (shape: BuildingShape) => {
        if (this.buildStroke || !BUILDING_SHAPES.includes(shape)) return
        this.buildingShape = shape
        this.buildingShapePaletteDismissed = true
        this.updateBuildingShapePalette()
    }

    private respondToBuildShapeWheel = (event: WheelEvent) => {
        if (!this.terrainPointerOn || !this.isKeyOn("Shift") || !this.mouseActionEnabled || this.isChat) return
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        if (gui.isItemScreenOpen() || gui.isCommandWheelVisible() || gui.isChatComposerVisible?.() || gui.isNamePromptVisible?.()) return
        event.preventDefault()
        if (this.buildingShapePaletteDismissed && !this.buildStroke) return
        const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX
        if (this.buildStroke || delta === 0) return
        if (Date.now() - this.buildingShapeScrollAt > 250) this.buildingShapeScroll = 0
        this.buildingShapeScrollAt = Date.now()
        this.buildingShapeScroll += delta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1)
        if (Math.abs(this.buildingShapeScroll) < 40) return
        const index = BUILDING_SHAPES.indexOf(this.buildingShape)
        this.selectBuildingShape(BUILDING_SHAPES[(index + (this.buildingShapeScroll > 0 ? 1 : -1) + BUILDING_SHAPES.length) % BUILDING_SHAPES.length])
        this.buildingShapeScroll = 0
    }

    updateBuildingShapePalette = () => {
        if (isMobile()) return
        const shift = this.isKeyOn("Shift")
        if (!shift) this.buildingShapePaletteDismissed = false
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        if (!gui.setBuildingShapePalette) return
        const item = this.entity.inventory?.items?.find(i => i.active && Mechanics.item.properties.buildable(i.itemTypeMeta))
        const buildType = item && Mechanics.item.properties.buildType(item.itemTypeMeta)
        const visible = this.terrainPointerOn && this.mouseActionEnabled && shift && !this.buildingShapePaletteDismissed && !this.isChat &&
            !gui.isItemScreenOpen() && !gui.isCommandWheelVisible() && !gui.isChatComposerVisible?.() && !gui.isNamePromptVisible?.() &&
            ["wall", "roof", "floor", "floor2"].includes(buildType)
        gui.setBuildingShapePalette?.({ visible: !!visible, shape: this.buildingShape, locked: !!this.buildStroke,
            hollow: buildType === "wall", position: this.mouseHandler.state().position })
    }

    private placeBuildTile = (togglePending = true, deferTimer = false, target?: Coordinate, refresh = true) => {
        if (!this.terrainPointerOn) return
        if (!target) this.refreshBuildPointer()
        const coordinate = { ...(target || this.buildPointer) }
        const key = `${coordinate.x},${coordinate.y}`
        if (this.pendingBuilds.has(key)) {
            if (togglePending && !this.pendingBuilds.get(key).committing) this.removePendingBuild(key)
            if (refresh) this.refreshBuildPointer(true)
            return
        }
        const item = this.entity.inventory?.items?.find(i => i.active && Mechanics.item.properties.buildable(i.itemTypeMeta))
        const buildType = item && Mechanics.item.properties.buildType(item.itemTypeMeta)
        if (buildType && ["wall", "roof", "floor", "floor2"].includes(buildType)) {
            const pending: PendingBuild = {
                coordinate, itemId: item.id, roomId: this.entity.roomId,
                sprites: new Set(), valid: false, committing: false,
            }
            this.pendingBuilds.set(key, pending)
            this.buildPreviewRevision++
            if (this.buildPreview && isEqual(this.buildPreview.coordinate, coordinate)) {
                this.renderPendingBuild(pending, this.buildPreview.deltas, this.buildPreview.valid)
            }
            if (!deferTimer) {
                pending.groupId = ++this.buildPrintGroupId
                this.startReleasedBuildGroup(pending.groupId)
            }
        } else {
            this.client.commitWorldEdits(coordinate)
        }
        this.updatePendingBuildFunding()
        if (refresh) this.playBuildSwing()
        if (refresh) {
            this.clearTerrainPointer()
            this.refreshBuildPointer(true)
            this.refreshPendingPreviews()
        }
    }

    private startPendingBuildTimer = (pending: PendingBuild) => {
        if (pending.startedAt !== undefined) return
        if (pending.groupId === undefined) pending.groupId = ++this.buildPrintGroupId
        const groupId = pending.groupId
        const existing = Array.from(this.pendingBuilds.values()).find(p => p.groupId === groupId && p.startedAt !== undefined)
        pending.startedAt = existing?.startedAt ?? Date.now()
        if (!this.pendingPrintTimers.has(groupId)) {
            this.pendingPrintTimers.set(groupId, setTimeout(() => this.printPendingGroup(groupId), BUILD_PENDING_MS))
        }
        this.syncBuildingPrintGroups()
    }

    private printPendingGroup = (groupId: number) => {
        this.updatePendingBuildFunding()
        const group = Array.from(this.pendingBuilds.values()).filter(p => p.groupId === groupId)
        if (!group.length) return
        // Invalid cells are excluded individually; legal cells still print as one batch.
        group.filter(p => !p.valid || p.unfunded).forEach(p => this.removePendingBuild(`${p.coordinate.x},${p.coordinate.y}`, false))
        const printable = group.filter(p => p.valid && !p.unfunded)
        const first = group[0]
        const current = this.entity.inventory?.items?.find(i => i.id === first.itemId)
        const valid = printable.length > 0 && current && current.quantity >= printable.length && printable.every(p =>
            p.valid && p.itemId === first.itemId && p.roomId === this.entity.roomId)
        if (valid) {
            printable.forEach(pending => {
                pending.committing = true
                pending.sprites.forEach(sprite => { sprite.alpha = 1 })
            })
            this.syncBuildingPrintGroups()
            // The group stays opaque until its single server acknowledgement arrives.
            this.pendingPrintTimers.set(groupId, setTimeout(() => {
                group.forEach(p => this.removePendingBuild(`${p.coordinate.x},${p.coordinate.y}`, false))
                this.refreshPendingPreviews()
            }, 5000))
            this.client.commitWorldEditGroup(printable.map(p => ({ ...p.coordinate })), first.itemId)
        } else {
            group.forEach(p => this.removePendingBuild(`${p.coordinate.x},${p.coordinate.y}`, false))
            this.refreshPendingPreviews()
            this.refreshBuildPointer(true)
        }
    }

    private startReleasedBuildGroup = (groupId: number) => {
        this.updatePendingBuildFunding()
        const group = Array.from(this.pendingBuilds.values()).filter(p => p.groupId === groupId)
        // A fast release can arrive before the server previews. Wait for their verdicts
        // instead of treating unanswered cells as illegal or starting an empty print.
        if (!group.length || group.some(p => !p.previewResolved)) return
        if (!group.some(p => p.valid)) {
            group.forEach(p => this.removePendingBuild(`${p.coordinate.x},${p.coordinate.y}`, false))
            this.refreshPendingPreviews()
            this.refreshBuildPointer(true)
            return
        }
        if (!group.some(p => p.valid && !p.unfunded)) return
        if (group.some(p => p.startedAt !== undefined)) return
        group.forEach(p => this.startPendingBuildTimer(p))
        this.sharePendingBuilds()
    }

    private releasePendingBuilds = () => {
        const held = Array.from(this.pendingBuilds.values()).filter(p => p.startedAt === undefined && p.groupId === undefined)
        if (!held.length) return
        const groupId = ++this.buildPrintGroupId
        held.forEach(pending => {
            pending.groupId = groupId
        })
        this.startReleasedBuildGroup(groupId)
    }

    cancelPendingPrinting = (groupId?: number) => {
        this.suppressAttack()
        this.stopFiring()
        Array.from(this.pendingBuilds.entries()).forEach(([key, pending]) => {
            if (!pending.committing && (groupId === undefined || pending.groupId === groupId)) {
                this.removePendingBuild(key, false)
            }
        })
        this.buildPreview = undefined
        this.refreshPendingPreviews()
        this.refreshBuildPointer(true)
        this.syncBuildingPrintGroups()
    }

    private syncBuildingPrintGroups = () => {
        const groups = new Map<number, { id: number; startedAt: number; duration: number; count: number; anchor: Coordinate; onCancel: () => void }>()
        this.pendingBuilds.forEach(pending => {
            if (pending.startedAt === undefined || pending.committing || !pending.valid || pending.unfunded) return
            const id = pending.groupId
            const existing = groups.get(id)
            if (existing) {
                existing.count++
                existing.anchor = { ...pending.coordinate }
            } else groups.set(id, {
                id, startedAt: pending.startedAt, duration: BUILD_PENDING_MS, count: 1,
                anchor: { ...pending.coordinate },
                onCancel: () => this.cancelPendingPrinting(id),
            })
        })
        this.client.clientGameLogic.clientRenderer.gui().setBuildingPrintGroups?.(Array.from(groups.values()))
    }

    private updatePendingBuildFunding = () => {
        let changed = false
        const remaining = new Map(this.entity.inventory?.items?.map(item => [item.id, item.quantity]) || [])
        this.pendingBuilds.forEach(pending => {
            const available = remaining.get(pending.itemId) || 0
            const needsMaterial = !pending.previewResolved || pending.valid
            const unfunded = !pending.committing && needsMaterial && available <= 0
            if (needsMaterial && !unfunded) remaining.set(pending.itemId, available - 1)
            if (pending.unfunded !== unfunded) changed = true
            pending.unfunded = unfunded
            pending.sprites.forEach(sprite => { sprite.visible = !unfunded })
            if (unfunded && !pending.shortageMarker) {
                // Amber corners and a minus badge distinguish shortages from the red illegal X.
                const marker = new Graphics()
                marker.lineStyle(2, 0xffc35a, 0.95)
                const edge = TileSize - 2, length = TileSize * 0.25
                for (const [x, y, dx, dy] of [[2, 2, 1, 1], [edge, 2, -1, 1], [2, edge, 1, -1], [edge, edge, -1, -1]]) {
                    marker.moveTo(x + dx * length, y)
                    marker.lineTo(x, y)
                    marker.lineTo(x, y + dy * length)
                }
                marker.beginFill(0x352a18, 0.9)
                marker.drawCircle(TileSize / 2, TileSize / 2, TileSize * 0.18)
                marker.endFill()
                marker.moveTo(TileSize * 0.4, TileSize / 2)
                marker.lineTo(TileSize * 0.6, TileSize / 2)
                marker.position.set(pending.coordinate.x * TileSize, pending.coordinate.y * TileSize)
                marker.zIndex = 100000.5
                marker.alpha = this.pendingBuildAlpha(pending)
                pending.shortageMarker = marker
                this.client.clientGameLogic.clientRenderer.map().getTileSurface().addChild(marker)
            } else if (!unfunded && pending.shortageMarker) {
                pending.shortageMarker.destroy()
                pending.shortageMarker = undefined
            }
        })
        if (changed) this.syncBuildingPrintGroups()
    }

    private renderPendingBuild = (pending: PendingBuild, deltas: MapEntry[], valid: boolean) => {
        if (pending.committing) return
        pending.valid = valid
        pending.previewResolved = true
        this.updatePendingBuildFunding()
        if (!valid) {
            // An illegal neighbour combination must not erase the stroke. Keep the last
            // server-resolved art and mark its cell, including cells with no valid art yet.
            pending.sprites.forEach(sprite => { sprite.tint = 0xff8174 })
            if (!pending.invalidMarker) {
                const marker = new Graphics()
                marker.lineStyle(2, 0xff8174, 1)
                marker.beginFill(0xff8174, 0.15)
                marker.drawRect(1, 1, TileSize - 2, TileSize - 2)
                marker.endFill()
                const inset = TileSize * 0.35
                marker.moveTo(inset, inset)
                marker.lineTo(TileSize - inset, TileSize - inset)
                marker.moveTo(TileSize - inset, inset)
                marker.lineTo(inset, TileSize - inset)
                marker.position.set(pending.coordinate.x * TileSize, pending.coordinate.y * TileSize)
                marker.zIndex = 100000.5
                marker.alpha = this.pendingBuildAlpha(pending)
                pending.invalidMarker = marker
                this.client.clientGameLogic.clientRenderer.map().getTileSurface().addChild(marker)
            }
            return
        }
        pending.invalidMarker?.destroy()
        pending.invalidMarker = undefined
        pending.sprites.forEach(sprite => { sprite.parent?.removeChild(sprite); sprite.destroy() })
        pending.sprites.clear()
        const map = this.client.clientGameLogic.clientRenderer.map()
        deltas.forEach(delta => {
            if (["blocking", "skyTrigger"].includes(delta.layerId) || !delta.context?.highZ) return
            // Neighbour deltas belong to their own pending ghosts, not this cell's animation.
            if (delta.context?.previewTarget === false) return
            const sprite = map.addTileAt(delta.x, delta.y, String(delta.tileSpec.tilePos), delta.layerId, true)?.sprite
            if (sprite) {
                sprite.alpha = this.pendingBuildAlpha(pending)
                sprite.visible = !pending.unfunded
                sprite.zIndex = 100000
                pending.sprites.add(sprite)
            }
        })
    }

    private removePendingBuild = (key: string, refresh = true) => {
        const pending = this.pendingBuilds.get(key)
        if (!pending) return
        pending.invalidMarker?.destroy()
        pending.shortageMarker?.destroy()
        pending.sprites.forEach(sprite => { sprite.parent?.removeChild(sprite); sprite.destroy() })
        this.pendingBuilds.delete(key)
        if (pending.groupId !== undefined && !Array.from(this.pendingBuilds.values()).some(p => p.groupId === pending.groupId)) {
            clearTimeout(this.pendingPrintTimers.get(pending.groupId))
            this.pendingPrintTimers.delete(pending.groupId)
        }
        this.syncBuildingPrintGroups()
        this.buildPreviewRevision++
        if (refresh) this.refreshPendingPreviews()
    }

    buildingCommitResult = (coordinate: Coordinate) => {
        const pending = this.pendingBuilds.get(`${coordinate.x},${coordinate.y}`)
        if (pending?.committing) {
            Array.from(this.pendingBuilds.entries()).forEach(([key, member]) => {
                if (member.groupId === pending.groupId) this.removePendingBuild(key, false)
            })
            this.refreshPendingPreviews()
        }
        this.refreshBuildPointer(true)
    }

    private clearPendingBuilds = () => {
        Array.from(this.pendingBuilds.keys()).forEach(key => this.removePendingBuild(key, false))
        this.sharePendingBuilds()
    }

    private sharePendingBuilds = () => {
        this.updatePendingBuildFunding()
        const pending = Array.from(this.pendingBuilds.values())
        if (!pending.length && !this.lastSharedBuildSnapshot) return
        const signature = JSON.stringify([this.entity.roomId, pending.map(p =>
            [p.coordinate, p.itemId, p.startedAt, p.unfunded])])
        if (signature === this.lastSharedBuildSnapshot &&
            (!pending.length || Date.now() - this.lastSharedBuildAt < 3000)) return
        this.lastSharedBuildSnapshot = signature
        this.lastSharedBuildAt = Date.now()
        this.client.shareBuildingPreview?.(this.entity.roomId, pending[0]?.itemId, pending.filter(p => !p.unfunded).map(p => ({
            coordinate: { ...p.coordinate }, itemId: p.itemId,
            elapsedMs: p.startedAt === undefined ? undefined : Math.max(0, Date.now() - p.startedAt),
        })))
    }

    updateSharedBuildingPreview = (entityId: EntityId, tiles: { coordinate: Coordinate; deltas: MapEntry[]; elapsedMs?: number }[]) => {
        if (entityId === this.entity.entityId) return
        this.removeSharedBuilds(entityId)
        if (!tiles?.length) return
        const map = this.client.clientGameLogic.clientRenderer.map()
        const ghosts: SharedBuild[] = tiles.map(tile => {
            const sprites = new Set<Sprite>()
            tile.deltas.forEach(delta => {
                const sprite = map.addTileAt(delta.x, delta.y, String(delta.tileSpec.tilePos), delta.layerId, true)?.sprite
                if (!sprite) return
                // Pure display objects: no terrain edits, hit targets, or cancellation handlers.
                sprite.interactive = false
                sprite.zIndex = 100000
                sprites.add(sprite)
            })
            return { sprites, startedAt: tile.elapsedMs === undefined ? undefined : Date.now() - tile.elapsedMs }
        })
        this.sharedBuilds.set(entityId, { receivedAt: Date.now(), tiles: ghosts })
        this.animatePendingBuilds()
    }

    private removeSharedBuilds = (entityId: EntityId) => {
        this.sharedBuilds.get(entityId)?.tiles.forEach(tile => tile.sprites.forEach(sprite => sprite.destroy()))
        this.sharedBuilds.delete(entityId)
    }

    private clearSharedBuilds = () => {
        Array.from(this.sharedBuilds.keys()).forEach(entityId => this.removeSharedBuilds(entityId))
    }

    private pendingBuildAlpha = (pending: PendingBuild) => {
        if (pending.committing) return 1
        return this.buildGhostAlpha(pending.startedAt)
    }

    private buildGhostAlpha = (startedAt?: number) => {
        if (startedAt === undefined) return BUILD_GHOST_ALPHA
        const elapsed = Date.now() - startedAt
        const progress = Math.min(1, elapsed / BUILD_PENDING_MS)
        // Keep the pulse clearly visible throughout the countdown, tapering only in the
        // final half-second so the group lands at full opacity when it commits.
        const pulse = Math.sin(elapsed * Math.PI * 2 / 500) * 0.22 * Math.min(1, (1 - progress) * 10)
        return Math.max(0.1, Math.min(1, BUILD_GHOST_ALPHA + (1 - BUILD_GHOST_ALPHA) * progress + pulse))
    }

    private requestBuildPreview = (coordinate: Coordinate) => {
        const pending = this.pendingBuilds.get(`${coordinate.x},${coordinate.y}`)
        const itemId = pending?.itemId || this.entity.inventory?.items?.find(i =>
            i.active && Mechanics.item.properties.buildable(i.itemTypeMeta))?.id
        const pendingCoordinates = Array.from(this.pendingBuilds.values()).filter(p => p.itemId === itemId).map(p => p.coordinate)
        if (pending) this.client.getBuildingTilePreview({ ...coordinate }, pendingCoordinates, this.buildPreviewRevision, pending.itemId)
        else this.client.getBuildingTilePreview({ ...coordinate }, pendingCoordinates, this.buildPreviewRevision)
    }

    private refreshPendingPreviews = () => {
        this.sharePendingBuilds()
        this.pendingBuilds.forEach(pending => {
            if (!pending.committing) this.requestBuildPreview(pending.coordinate)
        })
    }

    animatePendingBuilds = () => {
        this.updatePendingBuildFunding()
        this.sharedBuilds.forEach((preview, entityId) => {
            // A disconnected owner cannot send the clear snapshot. Heartbeats keep live
            // held previews visible; abandoned previews expire without touching the map.
            if (Date.now() - preview.receivedAt > 15000) {
                this.removeSharedBuilds(entityId)
                return
            }
            preview.tiles.forEach(tile => tile.sprites.forEach(sprite => {
                sprite.alpha = this.buildGhostAlpha(tile.startedAt)
                const surface = this.client.clientGameLogic.clientRenderer.map().getTileSurface()
                if (sprite.parent !== surface) surface.addChild(sprite)
            }))
        })
        this.pendingBuilds.forEach(pending => {
            const alpha = this.pendingBuildAlpha(pending)
            for (const marker of [pending.invalidMarker, pending.shortageMarker]) {
                if (!marker) continue
                marker.alpha = alpha
                const surface = this.client.clientGameLogic.clientRenderer.map().getTileSurface()
                if (marker.parent !== surface) surface.addChild(marker)
            }
            pending.sprites.forEach(sprite => {
                sprite.alpha = alpha
                const surface = this.client.clientGameLogic.clientRenderer.map().getTileSurface()
                if (sprite.parent !== surface) surface.addChild(sprite)
            })
        })
    }

    private placementItem = () => this.entity?.inventory?.items?.find(item =>
        item.active && item.quantity > 0 && Mechanics.item.properties.placement(item.itemTypeMeta) &&
        (item.readied || Mechanics.item.properties.buildable(item.itemTypeMeta)))

    private refreshBuildPointer = (force = false) => {
        if (isMobile()) {
            const buildable = this.entity?.inventory?.items?.some(i => i.active && Mechanics.item.properties.buildable(i.itemTypeMeta))
            if (buildable) {
                if (!this.terrainPointerOn) {
                    this.clearTerrainPointer()
                    return
                }
                const target = this.roughPointer || fineToRoughCoordinates(this.entity.location)
                this.updateBuildGridMarker(target)
                if (force || !this.buildPointer || !isEqual(this.buildPointer, target)) {
                    this.buildPointer = { ...target }
                    this.requestBuildPreview(target)
                }
                return
            }
        }
        const item = this.placementItem()
        if (item && !this.locationPick) {
            const { x, y } = this.entity.location
            const angle = this.entity.movement.angle
            if (item.id !== this.placementPointerItemId || !this.placementPointer || !this.placementPointerPose || x !== this.placementPointerPose.x ||
                y !== this.placementPointerPose.y || angle !== this.placementPointerPose.angle) {
                this.placementPointer = facingPlacementTarget(this.entity.location, angle)
            }
            this.placementPointerPose = { x, y, angle }
            this.placementPointerItemId = item.id
            this.updateBuildGridMarker(this.placementPointer)
            this.placementPointerOn = true
            this.client.clientGameLogic.clientRenderer.gui().createCursorSprite().visible = false
            const rule = Mechanics.item.properties.placement(item.itemTypeMeta)
            if (rule.action === "build" && this.terrainPointerOn &&
                (force || !this.buildPointer || !isEqual(this.buildPointer, this.placementPointer))) {
                this.buildPointer = { ...this.placementPointer }
                this.requestBuildPreview(this.buildPointer)
            }
            return
        }
        if (this.placementPointerOn) {
            if (this.buildGridMarker) this.buildGridMarker.visible = false
            this.placementPointerOn = false
        }
        this.placementPointer = undefined
        this.placementPointerPose = undefined
        this.placementPointerItemId = undefined
        if (!this.terrainPointerOn || !this.entity) return
        const target = this.roughPointer || fineToRoughCoordinates(this.entity.location)
        this.updateBuildGridMarker(target)
        if (force || !this.buildPointer || !isEqual(this.buildPointer, target)) {
            this.buildPointer = { ...target }
            this.requestBuildPreview(target)
        }
    }

    private updateBuildGridMarker = (target: Coordinate) => {
        if (!this.buildGridMarker) {
            const marker = new Graphics()
            marker.lineStyle(2, 0xffd166, 0.9)
            marker.beginFill(0xffd166, 0.15)
            // Inset the stroke so the outer edge exactly matches the tile's footprint.
            marker.drawRect(1, 1, TileSize - 2, TileSize - 2)
            marker.endFill()
            // Above terrain tilemaps, beneath the preview and world objects.
            marker.zIndex = 99
            this.buildGridMarker = marker
        }
        const marker = this.buildGridMarker
        const surface = this.client.clientGameLogic.clientRenderer.map().getTileSurface()
        if (marker.parent !== surface) surface.addChild(marker)
        marker.position.set(target.x * TileSize, target.y * TileSize)
        marker.visible = true
    }

    clearTerrainPointer = () => {
        if (this.buildGridMarker) this.buildGridMarker.visible = false
        this.terrainPointerSprites.forEach(sprite => sprite.parent?.removeChild(sprite))
        this.terrainPointerSprites.clear()
    }

    // Cosmetic feedback for placing a build tile: play the local player's attack (swing) animation.
    // Goes straight to the renderer's animation path (NOT clientGameLogic.attack, which would also
    // start a real server attack + audio), so there's no damage or gameplay effect. Other players
    // are shown the swing immediately through an animation-only server relay.
    playBuildSwing = () => {
        if (this.entity) {
            this.client.clientGameLogic.clientRenderer.entity().attack(this.entity)
            this.client.playBuildSwing?.(this.entity.roomId)
        }
    }

    updateTerrainPointer = (deltas: MapEntry[], coordinate?: Coordinate, blocked = false, illegal = false, previewRevision?: number, itemId?: string) => {
        if (previewRevision !== undefined && previewRevision !== this.buildPreviewRevision) return
        const target = coordinate || this.buildPointer
        if (!target) return
        const valid = !blocked && !illegal && !!deltas?.length
        const pending = this.pendingBuilds.get(`${target.x},${target.y}`)
        // Automatic server previews after a commit lack the pending-neighbour snapshot.
        if (pending && previewRevision === undefined) return
        if (pending && (!itemId || itemId === pending.itemId)) {
            this.renderPendingBuild(pending, deltas, valid)
            if (pending.groupId !== undefined && !pending.committing) this.startReleasedBuildGroup(pending.groupId)
        }
        if (!this.terrainPointerOn) return
        const activeItemId = this.entity.inventory?.items?.find(i => i.active && Mechanics.item.properties.buildable(i.itemTypeMeta))?.id
        if (itemId && itemId !== activeItemId) return
        // A delayed response for another cell must not move the current preview.
        if (!this.buildPointer || !isEqual(target, this.buildPointer)) return
        this.buildPreview = { coordinate: { ...target }, deltas, valid }
        this.clearTerrainPointer()
        this.updateBuildGridMarker(target)
        // The pending ghost already owns this cell; don't stack a second preview on it.
        if (pending || this.buildStroke?.area || blocked || !deltas?.length) return

        deltas.forEach((delta: MapEntry) => {
            if (delta.layerId === "blocking" || delta.layerId === "skyTrigger") {
                return
            }
            const result = this.client.clientGameLogic.clientRenderer
                .map()
                .addTileAt(delta.x, delta.y, String(delta.tileSpec.tilePos), delta.layerId)
            if (result.sprite) {
                this.terrainPointerSprites.add(result.sprite)
                result.sprite.alpha = 0.5
                if (delta?.context?.highZ) {
                    result.sprite.zIndex = 100000
                }
            }
        })
    }

    enabledMouseAction = (enabled: boolean) => {
        this.mouseActionEnabled = enabled
        if (!enabled) {
            if (this.mouseAttackHeld) this.stopFiring()
            // Whatever took the world's mouse away (a modal, a dialog) also took the pointer the
            // pick was waiting on, and respondToMouse stops running -- a reticle left up would just
            // hang wherever it was.
            this.cancelLocationPick()
        }
    }

    // Briefly suppress click-to-attack (strike / ranged / build). Called right after an interaction
    // is activated so the same press — or a rapid follow-up tap on the same entity (open then close a
    // chest) — can't fall through to a world attack. Needed because the attack click comes from a
    // document-level mousedown, which can't see that the press was claimed by an entity.
    /** A DOM control owns this interaction: cancel a held/charged attack without firing it. */
    cancelAttackForUi = () => {
        this.suppressAttack()
        this.stopFiring()
    }

    suppressAttack = (durationMs: number = 300) => {
        this.suppressAttackUntilTs = Date.now() + durationMs
    }

    /**
     * The entity under the pointer -- never YOURSELF.
     *
     * Hovering something is read as "you mean that creature, not the ground under it": it suppresses
     * the melee strike on an adjacent entity tile and sends a click to walk at the thing instead. Your
     * own sprite is an entity like any other, so standing on top of what you are trying to hit turned
     * every click into a no-op -- the strike was suppressed in favour of auto-travelling to yourself,
     * which goes nowhere and swings at nothing. Long grass is exactly where that bites: it does not
     * block, so you walk INTO a clump to reach it, which puts your own body under the cursor and makes
     * the grass uncuttable for as long as you stand there.
     *
     * Filtered here rather than at each use so auto-travel and the interaction wheel get it too; there
     * is nothing anywhere that wants to target the person doing the targeting.
     */
    setHoveredEntity = (entityId: EntityId) => {
        this.hoveredEntity = entityId === this.entity?.entityId ? undefined : entityId
    }

    unHoveredEntity = (entityId: EntityId) => {
        // Only clear if this is still the hovered entity. When the pointer moves
        // straight from one entity to another, the new entity's mouseover can run
        // before the old entity's mouseout; an unconditional clear would then wipe the
        // freshly-set hover and break click auto-travel.
        if (this.hoveredEntity === entityId) {
            this.hoveredEntity = undefined
        }
    }

    // Called when a hovered entity is removed or culled out of range. Releases the hover reference
    // and re-enables world mouse actions so they can't stay stuck disabled if the entity vanished
    // mid-press, and drops any press being arbitrated against it -- an entity that no longer exists
    // will never send the pointer-up that would otherwise resolve or cancel its press.
    clearHoverForEntity = (entityId: EntityId) => {
        this.cancelEntityPress(entityId)
        if (this.hoveredEntity === entityId) {
            this.hoveredEntity = undefined
            this.mouseActionEnabled = true
        }
    }

    isMouseActionEnabled = () => this.mouseActionEnabled

    enabledKeyboardAction = (enabled: boolean) => {
        this.keyboardActionEnabled = enabled
    }

    /**
     * Lock the player out of moving for `timeout`, or let them go again.
     *
     * THE LOCK IS A DEADLINE, not a timer, because the same screen transition raises it twice. A
     * teleport with a scene change asks for a long lock up front (the dissolve, the dark, the fade
     * back in) and then `ServerGameLogic.teleportEntity` sends its own plain fade-out when the move
     * actually happens -- and that one's default 1000ms used to expire FIRST and hand movement back
     * in the middle of a 1.5s dark hold. Keeping the furthest deadline means a later, shorter request
     * can never shorten a longer one; it can only extend it.
     */
    paralyze = (value: boolean, timeout: number = 1000) => {
        if (!value) {
            this.paralyzedUntilTs = 0
            this.paralyzed = false
            return
        }
        this.paralyzed = true
        const until = Date.now() + timeout
        if (until <= this.paralyzedUntilTs) {
            return
        }
        this.paralyzedUntilTs = until
        setTimeout(() => {
            // Only the request that owns the current deadline may lift it.
            if (Date.now() >= this.paralyzedUntilTs) {
                this.paralyzed = false
                this.paralyzedUntilTs = 0
            }
        }, timeout)
    }

    // Mirrors the server's DOWNED verdict. Every world-state message for this player carries it,
    // so it clears itself the moment the server stops saying it -- there is no client-side timer
    // to get out of step with, and nothing here that can hold a player down on its own.
    setDowned = (value: boolean) => {
        if (value === this.downed) {
            return
        }
        this.downed = value
        if (value) {
            this.cancelLocationPick()
            const { entity } = this
            entity.movement.moving = false
            entity.speed2.x = 0
            entity.speed2.y = 0
            this.targetSpeed2 = { x: 0, y: 0 }
            this.joystickSpeed2 = null
            clearInterval(this.autoMovingInterval)
            this.setAutoMoving(false)
        }
    }

    fireBullet = () => {
        this.client.fireBullet()
    }

    selectSlot = (itemId: string) => {
        this.client.clientGameLogic.selectSlot(itemId)
    }

    activeAbility = (shortcutKey: string) => {
        this.client.activateAbility(shortcutKey)
    }

    isGuest = () => this.guest

    setIsGuest = (guest: boolean) => {
        this.guest = guest
    }

    updateLongRangedEquippedState = (equipped: boolean) => {
        if (this.longRangedEquipped !== equipped) {
            this.longRangedEquipped = equipped

            const mouseSprite = this.client.clientGameLogic.clientRenderer.gui().createCursorSprite()
            if (!equipped && mouseSprite) {
                mouseSprite.visible = false
            }
        }
    }

    updateTerrainPointerOn = (on: boolean) => {
        this.mobileBuildDragging = false
        // Changing equipment ends the gesture, while its original material keeps printing.
        this.releasePendingBuilds()
        this.buildPreviewRevision++
        this.buildPreview = undefined
        this.terrainPointerOn = on
        this.buildPointer = undefined
        this.stopFiring()
        this.refreshPendingPreviews()
        if (on) {
            this.refreshBuildPointer(true)
            this.client.clientGameLogic.clientRenderer.gui().createCursorSprite().visible = false
        } else {
            this.clearTerrainPointer()
        }
    }

    // ---- location pick ---------------------------------------------------------------------------
    // "Click a spot on the ground, then do something with it." The defender's "Hold Position" order
    // is the first user: the wheel selects the order, this chooses the post, and only then does the
    // command go to the server (see GuiManager's onCommandSelect).
    //
    // The gesture deliberately sits at the same level as build mode rather than inside the command
    // wheel: both take the pointer away from attacking for one press, both are backed out of with
    // Escape, and both have to hold back the entity-press arbitration while they are armed.

    beginLocationPick = (request: LocationPickRequest) => {
        // Build mode and a location pick both own the pointer. The newer intent wins, and build mode
        // has to be ENDED server-side too, not just visually dropped (see the Escape path).
        if (this.terrainPointerOn) {
            this.updateTerrainPointerOn(false)
            this.client.clientGameLogic.cancelBuildPlacement()
        }
        // A press already being arbitrated against an entity would turn into an attack under the
        // reticle when its hold timer fired. Arming the pick ends it.
        this.releaseEntityPress()
        this.locationPick = request
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        const cursorSprite = gui.createCursorSprite()
        if (cursorSprite) {
            cursorSprite.visible = false
        }
        gui.showLocationPicker(request.caption, request.blockedCaption)
        gui.setLocationPickerBlocked(!this.locationPickable())
        // The press that picked the order off the command wheel is still travelling through the
        // document-level mouse listeners (pixi's pointerdown on the wheel runs first). Without this
        // it would arrive here as the pick itself and post the NPC under the wheel slice.
        this.suppressAttack()
    }

    cancelLocationPick = () => {
        if (!this.locationPick) {
            return
        }
        this.locationPick = undefined
        this.client.clientGameLogic.clientRenderer.gui().hideLocationPicker()
    }

    isPickingLocation = (): boolean => !!this.locationPick

    // The fine world coordinate under the pointer. Asked of the GUI rather than read off
    // `this.pointer`: that one is hand-derived from the camera modifiers and lifted ~10px so a bow
    // aims at a body rather than at the ground beneath it, which is the wrong bias for "stand here".
    private locationPickTarget = (): Coordinate | null =>
        this.client.clientGameLogic.clientRenderer.gui().worldPointAtPointer()

    // Could an NPC actually stand where the pointer is? A post inside a wall would leave the NPC
    // walking into it forever, so blocked ground is refused at the click instead of being sent and
    // quietly ignored -- and the reticle says so in red before the player commits to it.
    private locationPickable = (): boolean => {
        const point = this.locationPickTarget()
        if (!point) {
            return false
        }
        const tilemap = this.client.clientGameLogic.tilemapMetaProvider
        if (!tilemap) {
            return false
        }
        const rough = fineToRoughCoordinates(point)
        if (rough.x < 0 || rough.y < 0 || rough.x >= tilemap.width() || rough.y >= tilemap.height()) {
            return false
        }
        return !tilemap.isCollisionAt(rough.x, rough.y)
    }

    private completeLocationPick = () => {
        const request = this.locationPick
        if (!request) {
            return
        }
        const point = this.locationPickTarget()
        if (!point || !this.locationPickable()) {
            // A click on ground nothing can stand on is not an answer. Stay armed so the player can
            // just click somewhere else -- the reticle is already red, so it isn't a silent refusal.
            return
        }
        this.cancelLocationPick()
        request.onPick(roundCoordinates(point))
    }

    mobileBuildingState = () => {
        const item = this.entity?.inventory?.items?.find(i => i.active && i.quantity > 0 && Mechanics.item.properties.buildable(i.itemTypeMeta))
        return { available: !!item, active: isMobile() && this.terrainPointerOn, tool: this.mobileBuildTool,
            canUndo: Array.from(this.pendingBuilds.values()).some(p => !p.committing),
            material: item?.itemType || "Material" }
    }

    setMobileBuildMode = (on: boolean) => {
        if (!isMobile() || (on && !this.mobileBuildingState().available)) return
        this.endMobileBuildTouch()
        this.cancelLocationPick()
        this.updateTerrainPointerOn(on)
        this.suppressAttack()
    }

    selectMobileBuildTool = (tool: "single" | "line" | "room") => {
        if (!isMobile()) return
        this.endMobileBuildTouch()
        this.mobileBuildTool = tool
        this.suppressAttack()
    }

    undoMobileBuild = () => {
        if (!isMobile()) return
        this.endMobileBuildTouch()
        const pending = Array.from(this.pendingBuilds.values()).filter(p => !p.committing)
        const latest = pending[pending.length - 1]
        if (latest) this.cancelPendingPrinting(latest.groupId)
    }

    beginMobileBuildTouch = (coordinate: Coordinate) => {
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        if (!isMobile() || !this.terrainPointerOn || this.isChat || this.paralyzed || this.downed || this.entity.isDead() ||
            gui.isRolePickerVisible?.() || gui.isItemScreenOpen() || gui.isCommandWheelVisible() || gui.isChatComposerVisible?.() || gui.isNamePromptVisible?.()) return false
        this.mobileBuildDragging = true
        this.roughPointer = { ...coordinate }
        this.refreshBuildPointer(true)
        const key = `${coordinate.x},${coordinate.y}`
        const pending = this.pendingBuilds.get(key)
        if (pending && this.mobileBuildTool !== "single") {
            // Shape tools remove a tapped preview; freehand uses the desktop toggle-and-drag path.
            if (!pending.committing) {
                this.removePendingBuild(key)
                this.refreshBuildPointer(true)
            }
        } else {
            this.isFiring = true
            this.startWorldAction()
        }
        return true
    }

    moveMobileBuildTouch = (coordinate: Coordinate) => {
        if (!this.mobileBuildDragging) return
        this.roughPointer = { ...coordinate }
        // Freehand and shape tools share the desktop grid traversal, including sparse touch moves.
        this.updatedRoughPointer(coordinate)
    }

    endMobileBuildTouch = () => {
        if (!this.mobileBuildDragging) return
        this.mobileBuildDragging = false
        this.releasePendingBuilds()
        this.stopFiring()
    }

    builderActivation = (on: boolean) => {
        this.updateTerrainPointerOn(on)
    }

    restoreBuildingEquipment = () => {
        if (this.buildingEquipmentRestored) return
        this.buildingEquipmentRestored = true
        // Login restores equipment through the inventory snapshot, without an equip action.
        // Do this once per controller so later snapshots don't reset a pending stroke.
        const equipped = this.entity.inventory?.items?.some(item =>
            item.active && item.quantity > 0 && Mechanics.item.properties.buildable(item.itemTypeMeta))
        if (equipped && !this.terrainPointerOn) this.builderActivation(true)
    }

    manualAttack = (isMoving: boolean) => {
        if (this.entity.weapon?.profileId && !this.terrainPointerOn) {
            this.setAttackHeld(true)
            return
        }
        const consumable = this.entity.inventory?.items?.find(item =>
            item.active && item.readied && item.quantity > 0 &&
            Mechanics.item.properties.primaryUseable(item.itemTypeMeta))
        if (consumable) {
            const now = Date.now()
            if (this.lastPrimaryItemUseTs !== undefined &&
                now - this.lastPrimaryItemUseTs < WeaponCharacteristics.none.firingLatchMs) return
            this.lastPrimaryItemUseTs = now
            this.playBuildSwing()
            if (Mechanics.item.properties.placement(consumable.itemTypeMeta)) {
                this.refreshBuildPointer()
                this.client.inventoryAction(this.entity.entityId, consumable.id, "primary-use", this.placementPointer)
            } else {
                this.client.inventoryAction(this.entity.entityId, consumable.id, "primary-use")
            }
            return
        }
        if (this.terrainPointerOn) {
            this.placeBuildTile()
            return
        }
        logger.info({ tags: ["callback", "animation"] }, `manualAttack: Spacebar pressed, isMoving=${isMoving}`)

        const useMousePointer = Date.now() - this.lastMouseMoveTs < 150
        const collisionCheck = calculateNewCoordinateFromRadians(
            this.entity.location,
            this.entity.movement.angle,
            TileSize,
        )

        if (
            this.client.clientGameLogic.tilemapMetaProvider.isCollisionAt(
                fineToRough(collisionCheck.x),
                fineToRough(collisionCheck.y),
            )
        ) {
            logger.info({ tags: ["callback", "animation"] }, `manualAttack: Collision detected, firing bullet directly`)
            this.client.fireBullet()
            return
        }

        if (isMoving) {
            logger.info(
                { tags: ["callback", "animation"] },
                `manualAttack: Player is moving, firing bullet (no automatic boost - use Shift key for boost)`,
            )
            this.client.fireBullet()
            return
        }

        logger.info({ tags: ["callback", "animation"] }, `manualAttack: Player not moving, scheduling auto-travel`)

        const distance = this.longRangedEquipped ? 0 : TileSize * 1.5
        const thePoint = useMousePointer
            ? calculatePointAtDistance(this.entity.location, this.pointer, distance)
            : calculateNewCoordinateFromRadians(this.entity.location, this.entity.movement.angle, distance)

        this.scheduleAutoTravelToPoint(
            thePoint,
            () => {
                this.client.fireBullet()
            },
            false,
        )
    }

    setAutoMoving = (moving: boolean) => {
        this.isAutoMoving = moving
    }

    // The mobile joystick's held movement intent, as the same per-axis -1/0/1 vector a WASD
    // combination produces, or null when the thumb is off the stick. updatePlayerVelocity treats
    // it exactly like a held key. See GuiManager.setupJoystick for why it must be latched rather
    // than written straight onto entity.speed2.
    setJoystickIntent = (intent: Coordinate | null) => {
        const wasHeld = this.joystickSpeed2 !== null
        this.joystickSpeed2 = intent

        if (intent && !wasHeld) {
            // Started moving: take over from any auto-travel and start the same 700ms-then-inertia
            // ramp the keyboard path starts, so both input sources feel the same.
            clearInterval(this.autoMovingInterval)
            this.setAutoMoving(false)
            this.movementStartTime = Date.now()
            this.isSustainedMovement = false
        }
    }

    scheduleAutoTravelToEntity = (entity: Entity, travelCallback: Callback<void>) => {
        if (!entity) {
            return false
        }

        if (this.autoMovingInterval) {
            clearInterval(this.autoMovingInterval)
        }
        const { entity: playerEntity } = this

        const distance = getEntityDistance(playerEntity, entity)
        const tooClose = distance < 24
        const tooFar = distance > TileSize * 2

        if (!tooClose && !tooFar) {
            // console.log("distance", distance)
            this.autoTravelToEntity(entity, travelCallback)
            return true
        }

        return false
    }

    scheduleAutoTravelToPoint = (point: Coordinate, travelCallback: Callback<void>, restricted: boolean = true) => {
        if (!point) {
            return
        }

        if (this.autoMovingInterval) {
            clearInterval(this.autoMovingInterval)
        }
        const { entity: playerEntity } = this

        const distance = getDistance(point.x, point.y, playerEntity.location.x, playerEntity.location.y)
        const tooClose = distance < 24
        const tooFar = distance > TileSize * 2

        logger.debug(
            { tags: ["callback", "animation"] },
            `scheduleAutoTravelToPoint: distance=${distance}, tooClose=${tooClose}, tooFar=${tooFar}, restricted=${restricted}`,
        )

        if (!restricted || (!tooClose && !tooFar)) {
            logger.info(
                { tags: ["callback", "animation"] },
                `Starting auto-travel to point (${point.x}, ${point.y}) from (${playerEntity.location.x}, ${playerEntity.location.y})`,
            )
            this.autoTravelTo(point, travelCallback)
            return true
        }

        logger.debug({ tags: ["callback", "animation"] }, `Auto-travel blocked: tooClose=${tooClose}, tooFar=${tooFar}`)
        return false
    }

    autoTravelToEntity = (entity: Entity, arriveCallback?: Callback<void>) => {
        const targetCoordinate: Coordinate = floorCoordinates(entity.location)
        return this.autoTravelTo(targetCoordinate, arriveCallback)
    }

    autoTravelTo = (targetCoordinate: Coordinate, arriveCallback?: Callback<void>) => {
        const { entity: playerEntity } = this
        this.setAutoMoving(true)
        this.client.boost(true)

        logger.info(
            { tags: ["callback", "animation"] },
            `autoTravelTo: Starting travel to (${targetCoordinate.x}, ${targetCoordinate.y}) from (${playerEntity.location.x}, ${playerEntity.location.y}) - CALLBACK EXECUTED IMMEDIATELY`,
        )

        // BUG: This callback is executed immediately, not when arriving!
        arriveCallback()

        let startTs = Date.now()
        this.autoMovingInterval = setInterval(() => {
            const distance = Math.floor(getCoordinateDistance(playerEntity.location, targetCoordinate))
            const tooClose = distance < 24
            const tooFar = distance > TileSize * 2

            const isThere = isEqual(targetCoordinate, playerEntity.location)
            if (tooClose || isThere || tooFar) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `autoTravelTo: Arrived at destination! distance=${distance}, tooClose=${tooClose}, tooFar=${tooFar}, isThere=${isThere}`,
                )
                clearInterval(this.autoMovingInterval)
                this.setAutoMoving(false)

                this.targetSpeed2 = { x: 0, y: 0 }
                this.client.boost(false)

                return
            }

            if (Date.now() - startTs < 200) {
                return
            }

            // Set target speed2 for auto-travel
            this.targetSpeed2 = { x: 0, y: 0 }

            if (Math.floor(playerEntity.location.x) > targetCoordinate.x) {
                this.targetSpeed2.x = -1
            } else if (Math.floor(playerEntity.location.x) < targetCoordinate.x) {
                this.targetSpeed2.x = 1
            }

            if (Math.floor(playerEntity.location.y) > targetCoordinate.y) {
                this.targetSpeed2.y = -1
            } else if (Math.floor(playerEntity.location.y) < targetCoordinate.y) {
                this.targetSpeed2.y = 1
            }
        }, 5)
    }

    /**
     * The composite under the cursor, as (x, y) -- its PARENT cell, which is the one a strike is
     * aimed at and the only cell of it that carries the entity tile.
     *
     * The test is on that parent rather than on `roughPointer`, and the difference is the whole point
     * of the callback. A plant is taller than the cell it is rooted in: long grass is 1x2 and a tree
     * is bigger still, with the entity tile on the bottom cell and the art drawn over cells the map
     * array holds nothing in. CompositeTileRenderer resolves the art's geometry to its parent
     * without taking pointer events. Previously this asked the map about the cell the cursor was
     * literally over, found it empty, and CLEARED the pointer. So a click on the blades of a clump of
     * grass, or on a tree's canopy, sent no strike at all and fell through to walk-and-shoot: the
     * plant read as something that could not be cut rather than something being missed. Only the one
     * rooting cell worked, which for long grass is the thin strip under the leaves.
     *
     * A composite whose parent carries no entity tile (the decorative grass tufts, say) still clears,
     * which is what the renderer's own mouseout does by passing (-1, -1).
     */
    updateCompositeTile = (x: number, y: number) => {
        const entityTile =
            x > -1 && y > -1 ? this.client.clientGameLogic.tilemapMetaProvider.getEntityTileAt(x, y) : undefined
        if (entityTile) {
            this.roughCompositeTilePointer.x = x
            this.roughCompositeTilePointer.y = y
        } else {
            clearCoordinate(this.roughCompositeTilePointer)
        }
    }

    /**
     * Dismisses the camp's run hint once the player has actually RUN for a second.
     *
     * Running is the key held AND the body moving; neither alone is the lesson. Measured here rather
     * than on the keydown because that dismissed the hint on a keypress, and Shift is not only the
     * run key -- it opens the building shape palette too, so the hint disappeared unread in front of
     * a player laying their first wall.
     *
     * A satisfied clue is disarmed rather than merely hidden, so this cannot come back on a later
     * wave's countdown, and calling it when no hint is up is harmless. Done locally, with no round
     * trip, so the hint goes the instant it has been earned; the server decides separately whether
     * to raise it at all, from the same threshold in common (Mechanics.entity.speed.runLearnedMs).
     *
     * Time accumulates rather than needing to be unbroken -- see the mechanics constant for why --
     * and the step is clamped, because a backgrounded tab throttles this interval and must not come
     * back having silently banked the whole second.
     */
    private accrueRunHint = (isMoving: boolean) => {
        const threshold = Mechanics.entity.speed.runLearnedMs
        if (this.runHintMs >= threshold) {
            return
        }
        const now = Date.now()
        const previous = this.runHintLastTs
        this.runHintLastTs = now

        if (!isMoving || !this.isKeyOn("Shift")) {
            return
        }
        // No previous sample means this is the first tick of the run; nothing to credit yet.
        const elapsed = previous > 0 ? Math.min(now - previous, RUN_HINT_MAX_STEP_MS) : 0
        this.runHintMs += elapsed
        if (this.runHintMs >= threshold) {
            this.client.clientGameLogic.clientRenderer.gui()?.tutorialClues()?.satisfyClue("ember-run")
        }
    }

    startVelocityUpdates = () => {
        const updateInterval = 1000 / MovementInertiaConfig.UPDATE_FPS
        this.velocityUpdateInterval = setInterval(() => {
            this.updatePlayerVelocity()
            this.refreshBuildPointer()
            this.animatePendingBuilds()
            this.updateBuildingShapePalette()
            if (this.pendingBuilds.size) this.sharePendingBuilds()
        }, updateInterval)
    }

    stopVelocityUpdates = () => {
        if (this.velocityUpdateInterval) {
            clearInterval(this.velocityUpdateInterval)
            this.velocityUpdateInterval = null
        }
    }

    updatePlayerVelocity = () => {
        if (!this.entity || !MovementInertiaConfig.ENABLED) return

        // Stop movement if player is dead, downed or paralyzed. This loop OWNS entity.speed2, so
        // it is the one gate every kind of movement intent passes through -- held keys, a parked
        // joystick thumb, an auto-travel in progress.
        if (this.entity.isDead() || this.paralyzed || this.downed) {
            this.entity.speed2.x = 0
            this.entity.speed2.y = 0
            this.entity.movement.moving = false
            return
        }

        const friction = MovementInertiaConfig.FRICTION
        const minThreshold = MovementInertiaConfig.MIN_VELOCITY_THRESHOLD
        const acceleration = MovementInertiaConfig.ACCELERATION

        // Check if any keys are pressed
        const keyboardMoving =
            this.isKeyOn("w") ||
            this.isKeyOn("ArrowUp") ||
            this.isKeyOn("s") ||
            this.isKeyOn("ArrowDown") ||
            this.isKeyOn("a") ||
            this.isKeyOn("ArrowLeft") ||
            this.isKeyOn("d") ||
            this.isKeyOn("ArrowRight")

        // A held joystick is a held input too, and it owns the target vector while it is down:
        // its onChange fires only while the thumb actually moves, so nothing else would keep
        // targetSpeed2 current. The keyboard wins if somehow both are active.
        const joystick = this.joystickSpeed2
        const isMoving = keyboardMoving || joystick !== null
        this.accrueRunHint(isMoving)
        if (joystick && !keyboardMoving) {
            this.targetSpeed2 = this.entity.afk ? { x: 0, y: 0 } : { x: joystick.x, y: joystick.y }
        }

        // Check if we've been moving for more than 1 second
        const movementDuration = this.movementStartTime > 0 ? Date.now() - this.movementStartTime : 0
        const shouldUseInertia = movementDuration > 700

        if (isMoving) {
            // Update sustained movement state
            if (shouldUseInertia && !this.isSustainedMovement) {
                this.isSustainedMovement = true
            }

            if (shouldUseInertia) {
                // Keys are pressed and we've been moving for >1s - smoothly interpolate toward target
                const diffX = this.targetSpeed2.x - this.entity.speed2.x
                const diffY = this.targetSpeed2.y - this.entity.speed2.y

                this.entity.speed2.x += diffX * acceleration
                this.entity.speed2.y += diffY * acceleration
            } else {
                // Keys are pressed but <1s - immediate response
                this.entity.speed2.x = this.targetSpeed2.x
                this.entity.speed2.y = this.targetSpeed2.y
            }
        } else {
            // No keys pressed
            if (shouldUseInertia && this.isSustainedMovement) {
                // Apply friction to slow down only if we were in sustained movement
                this.entity.speed2.x *= friction
                this.entity.speed2.y *= friction

                // Stop movement if below threshold
                const speed = Math.sqrt(
                    this.entity.speed2.x * this.entity.speed2.x + this.entity.speed2.y * this.entity.speed2.y,
                )
                if (speed < minThreshold) {
                    this.entity.speed2.x = 0
                    this.entity.speed2.y = 0
                }
            } else {
                // Immediate stop for short movements
                this.entity.speed2.x = 0
                this.entity.speed2.y = 0
            }
        }

        // Update movement state
        const speed = Math.sqrt(
            this.entity.speed2.x * this.entity.speed2.x + this.entity.speed2.y * this.entity.speed2.y,
        )
        this.entity.movement.moving = speed > minThreshold
    }

    destroy = () => {
        this.stopFiring()
        this.cancelHeldInteraction()
        document.removeEventListener("wheel", this.respondToBuildShapeWheel)
        window.removeEventListener("blur", this.cancelHeldAttack)
        this.releaseEntityPress()
        this.terrainPointerOn = false
        this.updateBuildingShapePalette()
        this.clearSharedBuilds()
        this.clearPendingBuilds()
        this.clearTerrainPointer()
        this.buildGridMarker?.parent?.removeChild(this.buildGridMarker)
        this.buildGridMarker?.destroy()
        this.buildGridMarker = undefined
        // Hand the input back. A reconnect builds a NEW Player (ClientGameLogic.initializePlayer)
        // and the keyboard's listener list is static, so a controller that leaves its listeners
        // behind has every keystroke answered twice from then on -- which reads, in chat, as every
        // character you type appearing twice.
        this.keyboardHandler.removeListener(this.respondToKeyBoard)
        this.keyboardHandler.removePasteListener(this.respondToPaste)
        this.mouseHandler.removeListener(this.respondToMouse)
        this.stopVelocityUpdates()
        if (this.movementDaemon) {
            clearInterval(this.movementDaemon)
        }
        if (this.autoMovingInterval) {
            clearInterval(this.autoMovingInterval)
        }
        if (this.keyboardAndleAdjustInterval) {
            clearInterval(this.keyboardAndleAdjustInterval)
        }
        if (this.fireInterval) {
            clearInterval(this.fireInterval)
        }
        if (this.mousePointerFadeInterval) {
            clearTimeout(this.mousePointerFadeInterval)
        }
    }
}
